import { ReparoApi } from '@api';
import { getBomMaterialPicks, getBomProductPicks } from '@api/bom-management';
import { getCmSites } from '@api/cm-sites';
import {
    addShipment,
    amendedSinceSent,
    cancelMotherOrder,
    cloneMotherOrder,
    createMotherOrder,
    getMotherOrder,
    getWmsPushPlan,
    isOverDeliveryLine,
    listMotherOrders,
    markMotherOrderIssued,
    openMotherOrderPdf,
    pushOrderToWms,
    sendMotherOrder,
    updateMotherOrder,
    updateOrderExpectedDates,
    updateOrderPaymentTerms,
    WMS_STEP_LABEL,
    type CreateMotherOrderInput,
    type MotherOrder,
    type MotherOrderLine,
    OVER_DELIVERY_LINE_PREFIX,
    overDeliveryLineFor,
    type OrderReceipt,
    type WmsPushPlan,
} from '@api/mother-order';
import type { ProcurementOrder } from '@api/procurement-order';
import { listWarehousesForStn } from '@api/warehouse-ops';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { DateSinglePicker } from '@components/ui/DateRange';
import useToaster from '@hooks/toaster/useToaster';
import {
    ActionIcon,
    Box,
    Button,
    Checkbox,
    Chip,
    Group,
    Modal,
    NumberInput,
    Progress,
    Select,
    Stack,
    TagsInput,
    Text,
    Textarea,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { ArrowSquareOut, ListChecks, ShippingContainer, UploadSimple } from '@phosphor-icons/react';
import { destinationWarehouses, isQcDue, lineNeed, readinessFloor, shipmentNeeds, suggestLoads, fmtShortDate, type Need, type SuggestedLoad } from './inbound';
import { NeedChip, NeedChips } from './NeedChip';
import {
    getOrderEmailRecipients,
    getOrgConfig,
    orderEmailDefaultsFor,
} from '@api/organization';
import {
    getVendor,
    getVendorLanes,
    isSupplierVendor,
    listMaterialsForVendor,
    listProductsForVendor,
    listVendorIdsForMaterial,
    type VendorCapability,
} from '@api/vendors';
import { useCbmEnabled } from '@hooks/useCbmEnabled';
import usePersistentState from '@hooks/usePersistentState';
import { useAgreedLinePrices } from '@hooks/useAgreedLinePrices';
import { usePlaceOfSupplyFromBillTo } from '@hooks/usePlaceOfSupplyFromBillTo';
import { modals } from '@mantine/modals';
import { listWarehousesForPicker } from '@api/work-order';
import { peekNextPoNumber } from '@api/procurement-order';
import AddPrefixModal from '@components/containers/workOrders/AddPrefixModal';
import woClasses from '@components/containers/workOrders/NewProductionOrderModal.module.css';
import {
    GST_STATE_OPTIONS,
    groupTransfersIntoShipments,
    gstStateCodeFromStateName,
    placeOfSupplyFromGstin,
    type ShipmentGroup,
} from '@diagon-alley/common';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
    DocumentPreview,
    getOrganizationIdFromStorage,
    LineItemsTable,
    type PreviewProps,
} from '../procurementOrders/formComponents';
import DocumentAuthoringShell from '@components/ui/DocumentAuthoringShell';
import { LineItemsCsvModal } from '../procurementOrders/LineItemsCsvModal';
import ShipmentDetailPanel from '../shipments/ShipmentDetailPanel';
import shipmentStyles from './AddShipment.module.css';
import {
    draftFromSchedule,
    emptyPaymentTermsDraft,
    PaymentTermsCard,
    type PaymentTermsDraft,
    paymentTermsToPayload,
    templateSum,
} from './PaymentTermsCard';
import { PaymentScheduleSection } from './PaymentScheduleSection';
import { PaymentChip, summarizePayables } from '../payments';
import { getPayables, updateOrderLines } from '@api/mother-order';
import {
    type ChargeDraft,
    CURRENCY_OPTIONS,
    emptyCharge,
    emptyLine,
    mergeImportedLines,
    FieldLabel,
    formatDateDisplay,
    formatMoney,
    type LineDraft,
    SectionHeader,
    type SkuOption,
    StatusBadge,
    textColors,
    todayIso,
    type VendorOption,
    NO_SKU_OPTIONS,
    useReturnToWorkOrder,
    useWorkOrderPrefill,
    type WorkOrderPrefill,
    WorkOrderPrefillBanner,
    workOrderPrefillLine,
} from '../procurementOrders/shared';
import { formatCount } from '@utils/currency';

const num = (n: number): string => formatCount(n || 0);

/**
 * A number typed into a grid cell and committed when the person leaves it (blur / Enter),
 * never on every keystroke: each commit is a server round trip and a refetch of the order.
 */
const CommitNumberCell: FC<{
    value: number | null;
    max?: number;
    placeholder?: string;
    disabled?: boolean;
    onCommit: (value: number | null) => void;
}> = ({ value, max, placeholder, disabled, onCommit }) => {
    const [draft, setDraft] = useState<number | ''>(value ?? '');
    useEffect(() => setDraft(value ?? ''), [value]);
    const commit = () => {
        const next = draft === '' ? null : Number(draft);
        if ((next ?? null) !== (value ?? null)) onCommit(next);
    };
    return (
        <NumberInput
            value={draft}
            onChange={(v) => setDraft(typeof v === 'number' ? v : '')}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
            }}
            min={0}
            max={max}
            size="xs"
            placeholder={placeholder}
            disabled={disabled}
            hideControls
            classNames={{ input: shipmentStyles.qtyInput }}
        />
    );
};

const errMessage = (e: any, fallback: string): string =>
    e?.error?.message || e?.message || fallback;

// ─── Fulfilment bar ─────────────────────────────────────────────

// A single purple ramp (dark = furthest along, light = earliest stage) so the
// bar reads as progress and stays on the product's purple palette.
const FULFIL_COLORS = {
    received: 'rgb(91, 33, 182)', // violet-800
    inTransit: 'rgb(139, 92, 246)', // violet-500
    inProduction: 'rgb(216, 211, 247)', // light lavender
};

const LegendDot: FC<{ color: string; label: string }> = ({ color, label }) => (
    <Group gap={6}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />
        <Text size="12px" c={textColors.secondary}>
            {label}
        </Text>
    </Group>
);

const FulfilmentBar: FC<{ order: MotherOrder }> = ({ order }) => {
    const ordered = order.orderedUnits || 1;
    const seg = (n: number) => `${Math.max(0, (n / ordered) * 100)}%`;
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    height: 8,
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: 'rgb(241, 245, 249)',
                }}
            >
                <div style={{ width: seg(order.receivedUnits), background: FULFIL_COLORS.received }} />
                <div style={{ width: seg(order.inTransitUnits), background: FULFIL_COLORS.inTransit }} />
                <div style={{ width: seg(order.inProductionUnits), background: FULFIL_COLORS.inProduction }} />
            </div>
            <Group gap={16} mt={8}>
                <LegendDot color={FULFIL_COLORS.received} label={`Received ${num(order.receivedUnits)}`} />
                <LegendDot color={FULFIL_COLORS.inTransit} label={`In transit ${num(order.inTransitUnits)}`} />
                <LegendDot color={FULFIL_COLORS.inProduction} label={`In production ${num(order.inProductionUnits)}`} />
            </Group>
        </div>
    );
};

// ─── Add Shipment modal ─────────────────────────────────────────

/**
 * What pushing to the WMS creates at each step, per the merchant's push policy. Nulls when
 * there is no WMS or no policy — and every WMS control below hides itself on null, so a
 * merchant without a WMS never sees them.
 */
const NO_WMS_PLAN: WmsPushPlan = { wmsType: null, mode: null, order: null, shipment: null, receive: null, standaloneShipment: null, standaloneReceive: null, stn: null, distribution: null };
export const useWmsPushPlan = (): WmsPushPlan => {
    const { data } = useQuery(['wms-push-plan'], getWmsPushPlan, { staleTime: 5 * 60_000 });
    return data ?? NO_WMS_PLAN;
};

const fmtCbm = (n: number): string =>
    formatCount(n, { decimals: 2 });

/**
 * Packs order lines into containers at a cap — the shared routine the server splits
 * distribution plans with, so the estimate on an order and the shipment somebody then
 * creates against it never disagree about how a load rounds. Lines with no CBM are left
 * out: they cannot be measured, so they cannot be packed, and the screen says so beside
 * them rather than pretending they take no room.
 */
function packIntoContainers(
    lines: Array<{ key: string; name: string | null; quantity: number; unitCbm: number | null }>,
    cap: number,
): ShipmentGroup[] {
    return groupTransfersIntoShipments(
        lines
            .filter((l) => l.quantity > 0 && l.unitCbm != null && l.unitCbm > 0)
            .map((l) => ({
                transferId: l.key,
                fromId: 'order',
                toId: null,
                productId: l.key,
                productName: l.name,
                quantity: l.quantity,
                measurementValue: l.unitCbm,
            })),
        { maxUnitsPerShipment: null, maxMeasurementPerShipment: cap },
    );
}

export type AddShipmentPrefill = {
    qtys?: Record<string, number>;
    plannedDate?: string | null;
    warehouseId?: string | null;
    /** A suggested load's ready-by date: the latest line in it. */
    readinessDate?: string | null;
};

export const AddShipmentModal: FC<{
    order: MotherOrder | null;
    opened: boolean;
    onClose: () => void;
    onAdded: () => void;
    /** Pre-fill from a suggestion (Track): line qtys, the need-by date, the destination. */
    prefill?: AddShipmentPrefill;
}> = ({ order, opened, onClose, onAdded, prefill }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const [name, setName] = useState('');
    const [plannedDate, setPlannedDate] = useState('');
    const [readinessDate, setReadinessDate] = useState('');
    const [dispatchDate, setDispatchDate] = useState('');
    const [arrivalDate, setArrivalDate] = useState('');
    const [deliveryDate, setDeliveryDate] = useState('');
    const [warehouseId, setWarehouseId] = useState<string | null>(null);
    const [reference, setReference] = useState('');
    const [qtys, setQtys] = useState<Record<string, number | ''>>({});
    /**
     * The vendor's batch per line, off the packing list. One batch per SKU here — a SKU sent
     * in two batches is two shipments or a CSV; the field stays optional because most vendors
     * do not print one. Expiry lives on the batch, which is why it is asked for beside the code.
     */
    const [batches, setBatches] = useState<Record<string, { code: string; expiry: string; mfg: string }>>({});
    /**
     * Whether somebody has set the GRD by hand. Until they do, the date simply IS the floor
     * (the latest ready date among the lines with a quantity) and follows it both ways — take
     * a late line off and the date comes back down. A prefilled date (a suggested load) is
     * not a hand edit; only the picker is.
     */
    const [readinessTouched, setReadinessTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pushToWms, setPushToWms] = useState(false);
    const wmsPlan = useWmsPushPlan();
    const cbmEnabled = useCbmEnabled();
    /**
     * Batch, MFG date and expiry are asked for only when the brand tracks finished goods by
     * batch. For everyone else the columns are noise, and the lines go up with nulls.
     */
    const { data: orgForBatches } = useQuery(
        ['org-config', getOrganizationIdFromStorage()],
        () => getOrgConfig(getOrganizationIdFromStorage() as string),
        { enabled: opened && !!getOrganizationIdFromStorage() },
    );
    const batchTrackingOn = orgForBatches?.batchTracking?.FG === 'full';
    // The vendor's lanes: the container cap for the picked destination, and which of our
    // warehouses a shipment from this vendor can be sent to at all.
    const { data: vendorLanes } = useQuery(
        ['vendor-lanes', order?.vendorId],
        () => getVendorLanes(order?.vendorId as string),
        { enabled: opened && !!order?.vendorId },
    );
    /**
     * The cap this shipment is measured against: the destination lane's, until somebody types
     * over it for this one booking (a high-cube instead of the usual 40-foot). Whatever is
     * used is saved on the shipment, so a later change to the lane does not rewrite history.
     */
    const [cap, setCap] = useState<number | ''>('');
    const [capTouched, setCapTouched] = useState(false);
    const [csvOpen, setCsvOpen] = useState(false);
    useEffect(() => {
        if (capTouched) return;
        const lane = (vendorLanes ?? []).find((l) => l.warehouseId === warehouseId);
        setCap(lane?.maxCbmPerShipment ?? '');
    }, [vendorLanes, warehouseId, capTouched]);
    // A GRN can only be raised against a WMS purchase order that already exists, so under
    // PO_AT_ORDER the order must have been pushed first.
    const wmsNeedsOrderPush = wmsPlan.shipment === 'CREATE_GRN' && !order?.wmsPoCode;

    const { data: warehouses } = useQuery(['warehouses-for-shipment'], () =>
        listWarehousesForStn(),
    );
    const { options: destinationOptions, laneScoped } = useMemo(
        () => destinationWarehouses(warehouses ?? [], vendorLanes, warehouseId),
        [warehouses, vendorLanes, warehouseId],
    );
    const noLanesYet = !!order?.vendorId && vendorLanes !== undefined && !laneScoped;

    useEffect(() => {
        if (opened) {
            setName('');
            setPlannedDate(prefill?.plannedDate ?? '');
            setReadinessDate(prefill?.readinessDate ?? '');
            setDispatchDate('');
            setArrivalDate('');
            setDeliveryDate('');
            setWarehouseId(prefill?.warehouseId ?? null);
            setReference('');
            setQtys(prefill?.qtys ?? {});
            setBatches({});
            setReadinessTouched(false);
            setSaving(false);
            // The push policy is the rulebook: when it names a WMS document for a shipment,
            // the box starts ticked and the operator opts OUT, not in.
            setPushToWms(!!wmsPlan.shipment);
            setCap('');
            setCapTouched(false);
        }
    }, [opened, prefill, wmsPlan.shipment]);

    const lines = useMemo(() => order?.lines ?? [], [order]);

    /**
     * The shipment is ready no earlier than the last of its lines. The floor follows the
     * quantities typed, both ways, until somebody sets the date by hand: pull in a line that
     * is ready later and the date moves up with it; take it off again and the date comes back
     * down. A hand-set date is only ever raised to the floor, never lowered.
     */
    const floor = useMemo(() => readinessFloor(lines, qtys), [lines, qtys]);
    useEffect(() => {
        if (!opened || !floor) return;
        if (!readinessTouched) {
            if (readinessDate !== floor.date) setReadinessDate(floor.date);
            return;
        }
        if (!readinessDate || readinessDate < floor.date) setReadinessDate(floor.date);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only the floor moves the date
    }, [floor?.date, opened, readinessTouched]);

    const totalThis = lines.reduce(
        (sum, l) => sum + (Number(qtys[l.itemId]) || 0),
        0,
    );

    const capNumber = cbmEnabled && cap !== '' && Number(cap) > 0 ? Number(cap) : null;
    const shipmentCbm = useMemo(() => {
        let sum = 0;
        for (const l of lines) {
            const qty = Number(qtys[l.itemId]) || 0;
            if (l.volumeCbm == null || l.volumeCbm <= 0 || qty <= 0) continue;
            sum += qty * l.volumeCbm;
        }
        return sum;
    }, [lines, qtys]);
    const uncountedLines = lines.filter((l) => l.volumeCbm == null || l.volumeCbm <= 0).length;
    const capPct = capNumber ? (shipmentCbm / capNumber) * 100 : 0;
    const capColor = capPct > 100 ? 'red' : capPct > 90 ? 'yellow' : 'blue';

    /**
     * Fill exactly one container: walk the lines top to bottom at the cap, take the largest
     * whole number of units that still fits from the line that would cross it, leave the rest
     * at zero. Lines with no CBM stay at zero — they cannot be packed, only decided by hand.
     * Press it again on the next shipment and it packs the next container from what is left.
     */
    const fillToCap = () => {
        if (!capNumber) return;
        const first = packIntoContainers(
            lines.map((l) => ({ key: l.itemId, name: l.description, quantity: l.shippable, unitCbm: l.volumeCbm })),
            capNumber,
        )[0];
        const next: Record<string, number | ''> = {};
        for (const l of lines) next[l.itemId] = '';
        for (const gl of first?.lines ?? []) next[gl.transferId] = gl.quantity;
        setQtys(next);
    };

    const fillAll = () => {
        const next: Record<string, number | ''> = {};
        for (const l of lines) next[l.itemId] = l.shippable;
        setQtys(next);
    };

    const handleSave = async (opts?: { overCapConfirmed?: boolean }) => {
        if (!order) return;
        const payloadLines = lines
            .map((l) => ({
                materialId: l.materialId,
                productId: l.productId,
                quantity: Number(qtys[l.itemId]) || 0,
                batchCode: batchTrackingOn ? batches[l.itemId]?.code?.trim() || null : null,
                expiryDate:
                    batchTrackingOn && batches[l.itemId]?.code?.trim() ? batches[l.itemId]?.expiry || null : null,
                manufacturingDate:
                    batchTrackingOn && batches[l.itemId]?.code?.trim() ? batches[l.itemId]?.mfg || null : null,
            }))
            .filter((l) => l.quantity > 0);
        if (payloadLines.length === 0) {
            showToastError({ title: 'Enter a quantity for the shipment' });
            return;
        }
        if (!readinessDate) {
            showToastError({
                title: 'Goods readiness date (GRD) is required',
                subtitle:
                    'Set the date goods are (or will be) ready — a future date is fine.',
            });
            return;
        }
        if (floor && readinessDate < floor.date) {
            showToastError({
                title: `Goods readiness cannot be before ${formatDateDisplay(floor.date)}`,
                subtitle: `${floor.line.description || floor.line.itemId} is ready on that date. Move the date, or take that line off this shipment.`,
            });
            return;
        }
        const over = lines.filter((l) => (Number(qtys[l.itemId]) || 0) > l.shippable);
        if (over.length > 0) {
            const l = over[0];
            showToastError({
                title: `${l.description || l.itemId}: at most ${num(l.shippable)} can go on this shipment`,
                subtitle:
                    l.qcSource === 'BRAND'
                        ? `QC passed ${num(l.qcPassedQty ?? 0)}, ${num(l.shipped)} already shipped.`
                        : `${num(l.ordered)} ordered, ${num(l.shipped)} already shipped.`,
            });
            return;
        }
        // Over the cap warns and asks; it never blocks. Containers are sometimes loaded past
        // the nominal figure, and the total undercounts wherever a product has no CBM.
        if (capNumber && shipmentCbm > capNumber && !opts?.overCapConfirmed) {
            const over = shipmentCbm - capNumber;
            modals.openConfirmModal({
                title: 'This shipment is over the container cap',
                children: (
                    <Text size="sm">
                        {fmtCbm(shipmentCbm)} CBM against a cap of {capNumber}, over by {fmtCbm(over)} CBM. You can
                        go ahead, or take {fmtCbm(over)} CBM off the lines.
                    </Text>
                ),
                labels: { confirm: 'Create anyway', cancel: 'Go back' },
                zIndex: 10000,
                onConfirm: () => void handleSave({ overCapConfirmed: true }),
            });
            return;
        }
        setSaving(true);
        try {
            const wantsPush = pushToWms && !!wmsPlan.shipment && !wmsNeedsOrderPush;
            // One call: under PO_AT_ORDER the backend raises the GRN FIRST and records the
            // shipment under the GRN code; a WMS refusal means no shipment is created at all.
            await addShipment(order.id, {
                name: name.trim() || undefined,
                plannedDate: plannedDate || undefined,
                readinessDate,
                dispatchDate: dispatchDate || undefined,
                arrivalDate: arrivalDate || undefined,
                deliveryDate: deliveryDate || undefined,
                warehouseId: warehouseId || undefined,
                reference: reference.trim() || undefined,
                maxCbmPerShipment: capNumber ?? undefined,
                lines: payloadLines,
                pushToWms: wantsPush,
            });
            showToastSuccess({
                title: wantsPush
                    ? wmsPlan.shipment === 'CREATE_PO'
                        ? 'Shipment created · purchase order created in WMS'
                        : 'Shipment created under its WMS GRN code'
                    : 'Shipment created',
            });
            onAdded();
            onClose();
        } catch (e: any) {
            showToastError({
                title: pushToWms ? 'WMS refused — shipment not created' : 'Could not record shipment',
                subtitle: errMessage(e, 'Please retry.'),
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Add shipment" size="90vw" centered>
            {order && (
                <div className="space-y-3">
                    <Group grow>
                        <div>
                            <FieldLabel>Shipment name</FieldLabel>
                            <TextInput
                                placeholder={`${order.orderNumber}-S…`}
                                value={name}
                                onChange={(e) => setName(e.currentTarget.value)}
                            />
                        </div>
                        <div>
                            <FieldLabel>Reference / AWB</FieldLabel>
                            <TextInput
                                value={reference}
                                onChange={(e) => setReference(e.currentTarget.value)}
                            />
                        </div>
                    </Group>
                    <Group grow>
                        <div className={shipmentStyles.compactDate}>
                            <FieldLabel>Expected date (need-by)</FieldLabel>
                            <DateSinglePicker
                                value={plannedDate}
                                onChange={(v) => setPlannedDate(v || '')}
                                placeholder="When it's needed"
                                clearable
                            />
                        </div>
                        <div className={shipmentStyles.compactDate}>
                            <FieldLabel>Goods readiness date — GRD *</FieldLabel>
                            <DateSinglePicker
                                value={readinessDate}
                                onChange={(v) => {
                                    setReadinessTouched(true);
                                    setReadinessDate(v || '');
                                }}
                                placeholder="Required — future date OK"
                            />
                            {floor && (
                                <Text size="11px" c={readinessDate && readinessDate < floor.date ? 'rgb(185, 28, 28)' : textColors.muted} mt={2}>
                                    Earliest {formatDateDisplay(floor.date)}, set by {floor.line.description || floor.line.itemId}.
                                </Text>
                            )}
                        </div>
                    </Group>
                    <Group grow>
                        <div className={shipmentStyles.compactDate}>
                            <FieldLabel>Dispatch date</FieldLabel>
                            <DateSinglePicker
                                value={dispatchDate}
                                onChange={(v) => setDispatchDate(v || '')}
                                placeholder="Optional"
                                clearable
                            />
                        </div>
                        <div className={shipmentStyles.compactDate}>
                            <FieldLabel>At Destination Port</FieldLabel>
                            <DateSinglePicker
                                value={arrivalDate}
                                onChange={(v) => setArrivalDate(v || '')}
                                placeholder="Optional"
                                clearable
                            />
                        </div>
                    </Group>
                    <Group grow align="flex-start">
                        <div className={shipmentStyles.compactDate}>
                            <FieldLabel>Delivery date</FieldLabel>
                            <DateSinglePicker
                                value={deliveryDate}
                                onChange={(v) => setDeliveryDate(v || '')}
                                placeholder="Optional — arrival at your warehouse"
                                clearable
                            />
                        </div>
                        <div>
                            <FieldLabel>Destination warehouse</FieldLabel>
                            <Select
                                // Only our own warehouses receive a shipment, and only the ones
                                // this vendor has a lane to: the lane is what gives the timeline.
                                data={destinationOptions.map((w) => ({ value: w.warehouseId, label: w.name }))}
                                value={warehouseId}
                                onChange={setWarehouseId}
                                placeholder="Select to compute timeline"
                                classNames={{ input: shipmentStyles.dateLikeInput }}
                                searchable
                                clearable
                            />
                            {noLanesYet && (
                                <Text size="11px" c={textColors.muted} mt={4}>
                                    No lanes from {order?.vendorName || 'this vendor'} yet — add one on the Lanes page
                                </Text>
                            )}
                        </div>
                        {cbmEnabled && (
                            <div>
                                <FieldLabel>Container cap (CBM)</FieldLabel>
                                <NumberInput
                                    value={cap}
                                    onChange={(v) => {
                                        setCapTouched(true);
                                        setCap(typeof v === 'number' ? v : '');
                                    }}
                                    min={0}
                                    placeholder={warehouseId ? 'No cap on this lane' : 'Pick a destination'}
                                    classNames={{ input: shipmentStyles.dateLikeInput }}
                                    hideControls
                                />
                            </div>
                        )}
                    </Group>
                    <Text size="11px" c={textColors.muted}>
                        GRD is required. It identifies whether a shipment is in production or in transit.
                    </Text>

                    <div
                        className="flex items-center"
                        style={{
                            marginTop: 14,
                            gap: 8,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            color: textColors.secondary,
                            padding: '0 4px',
                        }}
                    >
                        <span style={{ flex: 2 }}>ITEM</span>
                        <span style={{ width: 110, textAlign: 'right' }}>SHIPPABLE</span>
                        <span style={{ width: 110, textAlign: 'right' }}>THIS SHIPMENT</span>
                        {batchTrackingOn && (
                            <>
                                <span style={{ width: 120 }}>BATCH</span>
                                <span style={{ width: 120 }}>MFG DATE</span>
                                <span style={{ width: 120 }}>EXPIRY</span>
                            </>
                        )}
                    </div>
                    {lines.map((l) => (
                        <div key={l.itemId} className="flex items-center" style={{ gap: 8, padding: '2px 4px' }}>
                            <span style={{ flex: 2, fontSize: 13, color: textColors.primary, lineHeight: 1.25 }}>
                                {l.description || l.itemId}
                                {l.description && l.description !== l.itemId && (
                                    // The id beneath the name: two lines can share a name (colourways
                                    // often do), and the id is what a packing list is checked against.
                                    <span style={{ display: 'block', fontSize: 11, color: textColors.muted, fontVariantNumeric: 'tabular-nums' }}>
                                        {l.itemId}
                                    </span>
                                )}
                            </span>
                            <span
                                style={{ width: 110, textAlign: 'right', fontSize: 13, color: textColors.secondary }}
                                title={
                                    l.qcSource === 'BRAND'
                                        ? `QC passed ${num(l.qcPassedQty ?? 0)} − ${num(l.shipped)} shipped`
                                        : `${num(l.ordered)} ordered − ${num(l.shipped)} shipped (no QC recorded; shipping counts as passed)`
                                }
                            >
                                {num(l.shippable)}
                                {l.expectedReadinessDate && (
                                    <span style={{ display: 'block', fontSize: 10, color: textColors.muted }}>
                                        ready {fmtShortDate(l.expectedReadinessDate)}
                                    </span>
                                )}
                            </span>
                            <div style={{ width: 110 }}>
                                <NumberInput
                                    value={qtys[l.itemId] ?? ''}
                                    onChange={(v) =>
                                        setQtys((prev) => ({
                                            ...prev,
                                            [l.itemId]: typeof v === 'number' ? v : '',
                                        }))
                                    }
                                    min={0}
                                    max={l.shippable}
                                    size="xs"
                                    classNames={{ input: shipmentStyles.qtyInput }}
                                    hideControls
                                />
                            </div>
                            {batchTrackingOn && (
                            <>
                            <div style={{ width: 120 }}>
                                <TextInput
                                    value={batches[l.itemId]?.code ?? ''}
                                    onChange={(e) => {
                                        const code = e.currentTarget.value;
                                        setBatches((prev) => ({
                                            ...prev,
                                            [l.itemId]: { code, expiry: prev[l.itemId]?.expiry ?? '', mfg: prev[l.itemId]?.mfg ?? '' },
                                        }));
                                    }}
                                    placeholder="Batch code"
                                    size="xs"
                                    classNames={{ input: shipmentStyles.qtyInput }}
                                />
                            </div>
                            <div style={{ width: 120 }}>
                                <TextInput
                                    type="date"
                                    value={batches[l.itemId]?.mfg ?? ''}
                                    onChange={(e) => {
                                        const mfg = e.currentTarget.value;
                                        setBatches((prev) => ({
                                            ...prev,
                                            [l.itemId]: { code: prev[l.itemId]?.code ?? '', expiry: prev[l.itemId]?.expiry ?? '', mfg },
                                        }));
                                    }}
                                    disabled={!batches[l.itemId]?.code?.trim()}
                                    size="xs"
                                    classNames={{ input: shipmentStyles.qtyInput }}
                                />
                            </div>
                            <div style={{ width: 120 }}>
                                <TextInput
                                    type="date"
                                    value={batches[l.itemId]?.expiry ?? ''}
                                    onChange={(e) => {
                                        const expiry = e.currentTarget.value;
                                        setBatches((prev) => ({
                                            ...prev,
                                            [l.itemId]: { code: prev[l.itemId]?.code ?? '', expiry, mfg: prev[l.itemId]?.mfg ?? '' },
                                        }));
                                    }}
                                    disabled={!batches[l.itemId]?.code?.trim()}
                                    size="xs"
                                    classNames={{ input: shipmentStyles.qtyInput }}
                                />
                            </div>
                            </>
                            )}
                        </div>
                    ))}
                    <Group justify="space-between" mt="sm">
                        <Group gap={4}>
                            <Tooltip label="Fill all remaining" withArrow>
                                <ActionIcon variant="subtle" color="gray" size="sm" onClick={fillAll} aria-label="Fill all remaining">
                                    <ListChecks size={16} />
                                </ActionIcon>
                            </Tooltip>
                            {cbmEnabled && capNumber && (
                                <Tooltip label="Fill to cap" withArrow>
                                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={fillToCap} aria-label="Fill to cap">
                                        <ShippingContainer size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            <Tooltip label="Upload CSV" withArrow>
                                <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => setCsvOpen(true)} aria-label="Upload CSV">
                                    <UploadSimple size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                        <Text size="13px" c={textColors.secondary}>
                            {num(totalThis)} units
                            {cbmEnabled && (
                                <span style={{ fontWeight: 600, color: capPct > 100 ? 'rgb(185, 28, 28)' : textColors.primary }}>
                                    {' · '}
                                    {fmtCbm(shipmentCbm)}
                                    {capNumber ? ` of ${capNumber}` : ''} CBM
                                </span>
                            )}
                        </Text>
                    </Group>
                    {cbmEnabled && capNumber && (
                        <Box mt={6}>
                            <Progress value={Math.min(100, capPct)} color={capColor} size="sm" />
                            {uncountedLines > 0 && (
                                <Text size="11px" c="rgb(180, 83, 9)" mt={4}>
                                    {uncountedLines} line{uncountedLines === 1 ? ' has' : 's have'} no CBM on the product and {uncountedLines === 1 ? 'is' : 'are'} not counted.
                                </Text>
                            )}
                        </Box>
                    )}

                    {/*
                      * Quantities from a CSV, for the 40-line shipment nobody wants to type. The
                      * catalogue modal is reused as-is with the order's own lines as its "catalogue",
                      * keyed by product id: a row that names a line fills that line's box, a row
                      * that names anything else is listed and skipped, and a figure above what is
                      * still in production is capped at it, since the backend would refuse it.
                      */}
                    <LineItemsCsvModal
                        opened={csvOpen}
                        onClose={() => setCsvOpen(false)}
                        skuOptions={lines.map((l) => ({
                            sku: l.itemId,
                            name: l.description ?? l.itemId,
                            kind: l.materialId ? 'material' : 'product',
                            id: l.itemId,
                        }))}
                        unmatchedReason="not on this order"
                        title="Fill shipment quantities from CSV"
                        intro={
                            <>
                                Columns: <b>product_id</b> and <b>shipment</b> (the quantity going on this
                                shipment). Each row must match a line on this order — anything else is
                                listed below and skipped.
                            </>
                        }
                        templateCsv={`product_id,shipment\n${lines[0]?.itemId ?? 'TSBAS12COR22001'},100\n`}
                        templateFileName="shipment-quantities-template.csv"
                        confirmLabel={(n) => `Fill ${n || ''} line${n === 1 ? '' : 's'}`}
                        onImport={(drafts) => {
                            const byItem = new Map(lines.map((l) => [l.itemId, l]));
                            const next = { ...qtys };
                            const capped: string[] = [];
                            for (const d of drafts) {
                                const id = d.productId ?? d.materialId;
                                const line = id ? byItem.get(id) : undefined;
                                if (!id || !line) continue;
                                const qty = Number(d.quantity) || 0;
                                if (qty > line.shippable) capped.push(line.description || id);
                                next[id] = Math.min(qty, line.shippable);
                            }
                            setQtys(next);
                            if (capped.length > 0) {
                                showToastError({
                                    title: `${capped.length} line${capped.length === 1 ? '' : 's'} capped at what is shippable`,
                                    subtitle:
                                        capped.slice(0, 3).join(', ') +
                                        (capped.length > 3 ? ` and ${capped.length - 3} more` : ''),
                                });
                            }
                        }}
                    />

                    {wmsPlan.shipment && (
                        <Box mt="md">
                            <FieldLabel>External actions</FieldLabel>
                            <Checkbox
                                label={`Push to WMS — will ${WMS_STEP_LABEL[wmsPlan.shipment]}${
                                    wmsPlan.shipment === 'CREATE_GRN' && order?.wmsPoCode
                                        ? ` against PO ${order.wmsPoCode}`
                                        : ''
                                }`}
                                description={
                                    wmsNeedsOrderPush
                                        ? 'Push the order to the WMS first — the GRN needs a WMS purchase order to receive against.'
                                        : undefined
                                }
                                checked={pushToWms && !wmsNeedsOrderPush}
                                disabled={wmsNeedsOrderPush}
                                onChange={(e) => setPushToWms(e.currentTarget.checked)}
                            />
                        </Box>
                    )}

                    <Group justify="flex-end" mt="md">
                        <Button variant="default" onClick={onClose} disabled={saving}>
                            Cancel
                        </Button>
                        <Button onClick={() => void handleSave()} loading={saving}>
                            Add shipment
                        </Button>
                    </Group>
                </div>
            )}
        </Modal>
    );
};

// ─── Shipments table (expandable rows showing line items) ───────

const SHIP_COL = { chevron: 24, status: 130, needs: 170, date: 120, qty: 100, cbm: 90, received: 140 };

/**
 * The deliveries recorded against an order at a manufacturing site, one per GRN.
 *
 * An undone delivery stays on the list, struck through: the order's history should show that
 * a receipt was recorded and taken back, not quietly lose it.
 */
const ReceiptsTable: FC<{ receipts: OrderReceipt[]; lines: MotherOrderLine[] }> = ({
    receipts,
    lines,
}) => {
    const { data: sitesData } = useQuery(['cm-sites'], () => getCmSites());
    const siteName = (warehouseId: string) =>
        sitesData?.sites.find((site) => site.warehouseId === warehouseId)?.name ?? warehouseId;
    const itemName = (itemId: string) =>
        lines.find((line) => line.itemId === itemId)?.description || itemId;

    const header = (label: string, width?: number, right = false) => (
        <span style={{ ...(width ? { width } : { flex: 1 }), textAlign: right ? 'right' : 'left' }}>
            {label}
        </span>
    );

    return (
        <div style={{ border: '1px solid rgb(226, 232, 240)', borderRadius: 6, overflow: 'hidden' }}>
            <div
                className="flex items-center"
                style={{
                    background: 'rgb(248, 250, 252)',
                    borderBottom: '1px solid rgb(226, 232, 240)',
                    padding: '8px 10px',
                    gap: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    color: textColors.secondary,
                }}
            >
                {header('GRN', 130)}
                {header('DATE', 100)}
                {header('RECEIVED AT', 190)}
                {header('CHALLAN', 120)}
                {header('ITEMS')}
                {header('RECEIVED', 90, true)}
                {header('REJECTED', 90, true)}
                {header('APPROVED', 90, true)}
                {header('ENTERED BY', 140)}
            </div>
            {receipts.map((receipt) => (
                <div
                    key={receipt.grnId}
                    className="flex items-center"
                    style={{
                        padding: '10px',
                        gap: 8,
                        fontSize: 13,
                        borderBottom: '1px solid rgb(241, 245, 249)',
                        color: receipt.reversed ? textColors.muted : textColors.primary,
                        textDecoration: receipt.reversed ? 'line-through' : undefined,
                    }}
                    title={receipt.reversed ? 'This delivery was undone' : undefined}
                >
                    <span style={{ width: 130, fontWeight: 600 }}>{receipt.grnId}</span>
                    <span style={{ width: 100 }}>{formatDateDisplay(receipt.date)}</span>
                    <span style={{ width: 190 }}>{siteName(receipt.warehouseId)}</span>
                    <span style={{ width: 120 }}>{receipt.reference ?? '—'}</span>
                    <span style={{ flex: 1 }}>
                        {receipt.itemIds.map(itemName).join(', ')}
                        {(() => {
                            // The receipt that brought more than the order asked for also wrote
                            // the line that keeps it; the line id names the receipt, so this is
                            // the receipt's own story told from the order's side.
                            const kept = overDeliveryLineFor(lines, receipt.grnId);
                            return kept ? (
                                <span style={{ display: 'block', fontSize: 11, color: 'rgb(180, 83, 9)' }}>
                                    {`${num(kept.ordered)} over the order, kept ${kept.unitPrice ? 'at the order rate' : 'free'}`}
                                </span>
                            ) : null;
                        })()}
                    </span>
                    <span style={{ width: 90, textAlign: 'right' }}>{num(receipt.received)}</span>
                    <span style={{ width: 90, textAlign: 'right' }}>{num(receipt.rejected)}</span>
                    <span style={{ width: 90, textAlign: 'right', fontWeight: 600 }}>
                        {num(receipt.received - receipt.rejected)}
                    </span>
                    <span style={{ width: 140 }}>{receipt.enteredByName ?? '—'}</span>
                </div>
            ))}
        </div>
    );
};

const ShipmentsTable: FC<{
    shipments: ProcurementOrder[];
    onSelect: (shipment: ProcurementOrder) => void;
}> = ({ shipments, onSelect }) => {
    const showCbm = useCbmEnabled();
    return (
    <div style={{ border: '1px solid rgb(226, 232, 240)', borderRadius: 6, overflow: 'hidden' }}>
        <div
            className="flex items-center"
            style={{
                background: 'rgb(248, 250, 252)',
                borderBottom: '1px solid rgb(226, 232, 240)',
                padding: '8px 10px',
                gap: 8,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: textColors.secondary,
            }}
        >
            <span style={{ flex: 1 }}>SHIPMENT</span>
            <span style={{ width: SHIP_COL.status }}>STATUS</span>
            <span style={{ width: SHIP_COL.needs }}>NEEDS</span>
            <span style={{ width: SHIP_COL.date }}>DISPATCH</span>
            <span style={{ width: SHIP_COL.date }}>AT DEST. PORT</span>
            <span style={{ width: SHIP_COL.qty, textAlign: 'right' }}>SHIPMENT QTY</span>
            {showCbm && <span style={{ width: SHIP_COL.cbm, textAlign: 'right' }}>CBM</span>}
            <span style={{ width: SHIP_COL.received, textAlign: 'right' }}>RECEIVED</span>
        </div>

        {shipments.map((s) => {
            const total = Number(s.totalUnits ?? 0);
            const received = Number(s.receivedUnits ?? 0);
            return (
                <div
                    key={s.id}
                    className="flex items-center"
                    onClick={() => onSelect(s)}
                    style={{
                        padding: '10px',
                        gap: 8,
                        borderBottom: '1px solid rgb(241, 245, 249)',
                        cursor: 'pointer',
                    }}
                >
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: textColors.primary }}>
                        {s.poNumber}
                    </span>
                    <span style={{ width: SHIP_COL.status }}>
                        <StatusBadge status={s.status} />
                    </span>
                    <span style={{ width: SHIP_COL.needs }}>
                        <NeedChips needs={shipmentNeeds(s)} />
                    </span>
                    <span style={{ width: SHIP_COL.date, fontSize: 13, color: s.shippedAt ? textColors.primary : textColors.secondary }}
                          title={s.shippedAt ? 'Dispatched' : s.departureDate ? 'Planned, not yet marked dispatched' : undefined}>
                        {s.departureDate ? formatDateDisplay(s.departureDate) : '—'}
                        {s.departureDate && !s.shippedAt ? <span style={{ fontSize: 10, color: textColors.muted }}> plan</span> : null}
                    </span>
                    <span style={{ width: SHIP_COL.date, fontSize: 13, color: textColors.primary }}>
                        {s.arrivalDate ? formatDateDisplay(s.arrivalDate) : '—'}
                    </span>
                    <span style={{ width: SHIP_COL.qty, textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                        {num(total)}
                    </span>
                    {showCbm && (
                        <span style={{ width: SHIP_COL.cbm, textAlign: 'right', fontSize: 13, color: s.totalCbm == null ? textColors.muted : textColors.primary, fontVariantNumeric: 'tabular-nums' }}>
                            {s.totalCbm == null ? '—' : fmtCbm(s.totalCbm)}
                        </span>
                    )}
                    <span style={{ width: SHIP_COL.received, textAlign: 'right', fontSize: 13, color: received > 0 ? textColors.primary : textColors.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {num(received)}
                    </span>
                </div>
            );
        })}
    </div>
    );
};

// ─── Order detail modal ─────────────────────────────────────────

/**
 * A ready date that a shipment has already built on. The server refuses to change it while
 * units of the line are shipped; a shipped line with no date yet may still be given one.
 */
const isReadyDateLocked = (line: Pick<MotherOrderLine, 'shipped' | 'expectedReadinessDate'>): boolean =>
    line.shipped > 0 && !!line.expectedReadinessDate;

const READY_DATE_LOCKED_HINT = 'Set by shipment — cannot be changed';

const OrderDetailModal: FC<{
    order: MotherOrder | null;
    loading: boolean;
    opened: boolean;
    onClose: () => void;
    onChanged: () => void;
    onAddShipment: (prefill?: AddShipmentPrefill) => void;
    onEdit: (order: MotherOrder) => void;
    /** Open the send dialog as soon as the order is in - a receipt that amended it sent us here. */
    sendOnOpen?: boolean;
    onSendOpened?: () => void;
}> = ({ order, loading, opened, onClose, onChanged, onAddShipment, onEdit, sendOnOpen = false, onSendOpened }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const [busy, setBusy] = useState<string | null>(null);
    const wmsPlan = useWmsPushPlan();
    const cbmEnabled = useCbmEnabled();
    const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(null);
    // The vendor's lanes carry the fill threshold the suggested loads nudge against.
    const { data: detailLanes } = useQuery(
        ['vendor-lanes', order?.vendorId],
        () => getVendorLanes(order?.vendorId as string),
        { enabled: opened && !!order?.vendorId },
    );
    const minFillPct = useMemo(() => {
        const pcts = (detailLanes ?? [])
            .map((l) => l.minFillPct)
            .filter((p): p is number => p != null && p > 0);
        return pcts.length ? Math.max(...pcts) : null;
    }, [detailLanes]);
    const loads = useMemo<SuggestedLoad[]>(
        () =>
            order && order.status !== 'DRAFT' && order.status !== 'CANCELLED'
                ? suggestLoads(order.lines, {
                      cap: cbmEnabled ? order.maxCbmPerShipment : null,
                      minFillPct,
                  })
                : [],
        [order, cbmEnabled, minFillPct],
    );
    // Containers this order needs, packed line by line at the vendor's cap — the same routine
    // the shipment form fills a container with, so the two figures agree.
    const containersNeeded = useMemo(() => {
        if (!order || !cbmEnabled || !order.maxCbmPerShipment || order.totalCbm == null) return null;
        return packIntoContainers(
            order.lines.map((l) => ({ key: l.itemId, name: l.description, quantity: l.ordered, unitCbm: l.volumeCbm })),
            order.maxCbmPerShipment,
        ).length;
    }, [order, cbmEnabled]);
    // Re-derive from the (refetched) order so the panel stays fresh after edits.
    const selectedShipment =
        order?.shipments.find((s) => s.poNumber === selectedShipmentId) ?? null;

    /*
     * Sending is now an act with a dialog rather than a bare click.
     *
     * IT HAD NEITHER A RECIPIENT NOR A COPY LIST. The button emailed whatever `vendor_email`
     * held and copied only the sender, with nothing on screen saying where it was going — the
     * same gap the production order dialog was built to close, and its comment explains why:
     * an issue you cannot see the recipient of is indistinguishable from nothing happening.
     */
    const [sendOpen, setSendOpen] = useState(false);
    const [sendTo, setSendTo] = useState<string[]>([]);
    const [sendCc, setSendCc] = useState<string[]>([]);
    const [updateOrderAddress, setUpdateOrderAddress] = useState(false);

    const emailConfig = useQuery({
        queryKey: ['order-email-recipients', 'purchase-order'],
        queryFn: () => getOrderEmailRecipients('purchase-order'),
    });

    const openSend = () => {
        // The saved book first, falling back to the order's recorded contact. Who orders to
        // this supplier go to, and who the order is with, are different questions.
        const defaults = orderEmailDefaultsFor(emailConfig.data, order?.vendorId ?? null);
        setSendTo(
            defaults.to.length > 0
                ? defaults.to
                : order?.vendorEmail
                  ? [order.vendorEmail]
                  : [],
        );
        setSendCc(defaults.cc);
        setUpdateOrderAddress(false);
        setSendOpen(true);
    };

    useEffect(() => {
        if (!opened || !order || !sendOnOpen || emailConfig.isLoading) return;
        openSend();
        onSendOpened?.();
        // openSend reads the latest config and order each time; it is not a dependency to track.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, order?.id, sendOnOpen, emailConfig.isLoading]);

    const run = async (
        action: string,
        fn: () => Promise<unknown>,
        successTitle: string,
    ) => {
        setBusy(action);
        try {
            await fn();
            showToastSuccess({ title: successTitle });
            onChanged();
        } catch (e: any) {
            showToastError({ title: 'Action failed', subtitle: errMessage(e, 'Please retry.') });
        } finally {
            setBusy(null);
        }
    };

    /**
     * Readiness and QC are written straight from the grid: one cell, one call. The whole
     * order is refetched after, so shippable and the chips follow without local bookkeeping.
     */
    const saveLine = (
        line: MotherOrderLine,
        patch: { expectedReadinessDate?: string | null; qcPassedQty?: number | null; qcRejectedQty?: number | null },
        successTitle: string,
    ) =>
        order &&
        run(
            `line:${line.itemId}`,
            () => updateOrderLines(order.id, [{ itemId: line.itemId, lineItemId: line.lineItemId, ...patch }]),
            successTitle,
        );
    /**
     * A ready date a shipment has already built on stays put: the server refuses to change it
     * while units are shipped, so only the open lines are sent — the ones with no date yet,
     * or nothing on a shipment.
     */
    const openLines = order ? order.lines.filter((l) => !isReadyDateLocked(l)) : [];
    const everyReadyDateLocked = !!order && order.lines.length > 0 && openLines.length === 0;
    const setAllReadiness = (iso: string) =>
        order &&
        openLines.length > 0 &&
        run(
            'lines:ready-all',
            () =>
                updateOrderLines(
                    order.id,
                    openLines.map((l) => ({ itemId: l.itemId, lineItemId: l.lineItemId, expectedReadinessDate: iso || null })),
                ),
            iso
                ? openLines.length === order.lines.length
                    ? 'Ready date set on every line'
                    : `Ready date set on ${openLines.length} of ${order.lines.length} lines`
                : 'Ready dates cleared',
        );
    const linesEditable = !!order && order.status !== 'DRAFT' && order.status !== 'CANCELLED';

    const lineColumns: DataSheetColumn<MotherOrderLine>[] = useMemo(
        () => [
            { key: 'description', header: 'Item', width: 240, sticky: true,
              render: (v: string | null, r: MotherOrderLine) => (
                  <span style={{ fontSize: 13, color: textColors.primary, lineHeight: 1.25 }}>
                      {/*
                        * An over-delivery line's stored description carries the challan and GRN
                        * for the vendor's document; on screen the item reads by its own name and
                        * the chip beneath says what the line is and which receipt brought it.
                        */}
                      {isOverDeliveryLine(r.lineItemId)
                          ? (order?.lines.find((l) => l.itemId === r.itemId && !isOverDeliveryLine(l.lineItemId))?.description || v || r.itemId)
                          : (v || r.itemId)}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: textColors.muted, fontVariantNumeric: 'tabular-nums' }}>
                          {r.itemId}
                          {isOverDeliveryLine(r.lineItemId) && (
                              <NeedChip
                                  need={{
                                      label: 'Over-delivery',
                                      tone: 'warn',
                                      // The receipt lives in the hover and in the RECEIPTS table below;
                                      // the column is too narrow to carry a GRN number beside the id.
                                      title: `Received above the order on ${r.lineItemId.slice(OVER_DELIVERY_LINE_PREFIX.length)} and kept at the gate; that receipt added this line to the order.`,
                                  }}
                              />
                          )}
                      </span>
                  </span>
              ) },
            { key: 'ordered', header: 'Ordered', width: 110, align: 'right' as const,
              render: (v: number, r: MotherOrderLine) =>
                  isOverDeliveryLine(r.lineItemId) ? (
                      // Added, not asked for: the sign says this quantity was never on the order.
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'rgb(180, 83, 9)' }} title="Over the order as issued">{`+${num(v)}`}</span>
                  ) : (
                      <span style={{ fontSize: 13 }}>{num(v)}</span>
                  ) },
            { key: 'inProduction', header: 'In Production', width: 120, align: 'right' as const,
              render: (v: number) => <span style={{ fontSize: 13, color: textColors.primary }}>{num(v)}</span> },
            { key: 'inTransit', header: 'In transit', width: 110, align: 'right' as const,
              render: (v: number) => <span style={{ fontSize: 13, color: textColors.primary }}>{num(v)}</span> },
            // What has physically arrived against the line, and what it still owes - the same
            // figures a site receiving against this order is capped by.
            { key: 'received', header: 'Received', width: 100, align: 'right' as const,
              render: (v: number) => <span style={{ fontSize: 13, color: v > 0 ? textColors.primary : textColors.muted }}>{num(v)}</span> },
            { key: '_open', header: 'Open', width: 90, align: 'right' as const,
              csvValue: (_v: unknown, r: MotherOrderLine) => Math.max(0, r.ordered - r.received),
              render: (_v: unknown, r: MotherOrderLine) => (
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{num(Math.max(0, r.ordered - r.received))}</span>
              ) },
            { key: 'expectedReadinessDate', header: 'Ready on', width: 150,
              render: (v: string | null, r: MotherOrderLine) => {
                  if (linesEditable && isReadyDateLocked(r)) {
                      // A shipment has built on this date; the server would refuse the change.
                      return (
                          <Tooltip label={READY_DATE_LOCKED_HINT} withArrow>
                              <span style={{ fontSize: 13, color: textColors.muted, cursor: 'default' }}>{formatDateDisplay(v as string)}</span>
                          </Tooltip>
                      );
                  }
                  return linesEditable ? (
                      <DateSinglePicker
                          value={v ?? ''}
                          onChange={(iso) => void saveLine(r, { expectedReadinessDate: iso || null }, iso ? 'Ready date set' : 'Ready date cleared')}
                          placeholder="Set date"
                          clearable
                      />
                  ) : (
                      <span style={{ fontSize: 13, color: v ? textColors.primary : textColors.muted }}>{v ? formatDateDisplay(v) : '—'}</span>
                  );
              } },
            { key: 'qcPassedQty', header: 'QC passed', width: 110, align: 'right' as const,
              render: (v: number | null, r: MotherOrderLine) =>
                  linesEditable ? (
                      <CommitNumberCell
                          value={r.qcSource === 'BRAND' ? v : null}
                          max={r.ordered}
                          placeholder={r.qcSource === 'ASSUMED' && v != null ? `${num(v)} assumed` : '—'}
                          onCommit={(n) => void saveLine(r, { qcPassedQty: n, ...(n === null ? { qcRejectedQty: null } : {}) }, n === null ? 'QC cleared' : 'QC recorded')}
                      />
                  ) : (
                      <span style={{ fontSize: 13 }}>{v == null ? '—' : num(v)}</span>
                  ) },
            { key: 'qcRejectedQty', header: 'QC rejected', width: 110, align: 'right' as const,
              render: (v: number | null, r: MotherOrderLine) =>
                  linesEditable ? (
                      <CommitNumberCell
                          value={r.qcSource === 'BRAND' ? v : null}
                          max={r.ordered}
                          placeholder="—"
                          onCommit={(n) => void saveLine(r, { qcRejectedQty: n ?? 0, ...(r.qcSource !== 'BRAND' ? { qcPassedQty: r.qcPassedQty ?? 0 } : {}) }, 'QC recorded')}
                      />
                  ) : (
                      <span style={{ fontSize: 13 }}>{v == null ? '—' : num(v)}</span>
                  ) },
            { key: 'shippable', header: 'Shippable', width: 100, align: 'right' as const,
              render: (v: number, r: MotherOrderLine) => (
                  <span style={{ fontSize: 13, fontWeight: 600, color: v > 0 ? textColors.primary : textColors.muted }}
                        title={r.qcSource === 'BRAND' ? `QC passed ${num(r.qcPassedQty ?? 0)} − shipped ${num(r.shipped)}` : `${num(r.ordered)} ordered − shipped ${num(r.shipped)}`}>
                      {num(v)}
                  </span>
              ) },
            { key: 'need', header: 'Needs', width: 130,
              render: (_v: unknown, r: MotherOrderLine) => <NeedChip need={lineNeed(r)} /> },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps -- saveLine closes over `order`, which drives linesEditable
        [linesEditable, order?.id, order?.lines],
    );

    // The order's checklist, read off the data: what is done, what is waiting on whom.
    const rail = useMemo(() => {
        if (!order) return [];
        const lines = order.lines;
        const dated = lines.filter((l) => l.expectedReadinessDate).length;
        const judged = lines.filter((l) => l.qcSource === 'BRAND').length;
        const shipments = order.shipments.filter((s) => !s.cancelledAt);
        const booked = shipments.filter((s) => s.forwarderVendorId).length;
        const dispatched = shipments.filter((s) => s.shippedAt).length;
        const termsDone = !!order.paymentSchedule?.template?.length;
        return [
            { label: 'Terms', value: termsDone ? 'chosen' : 'not chosen', done: termsDone },
            { label: 'Ready dates', value: `${dated} of ${lines.length}`, done: lines.length > 0 && dated === lines.length },
            { label: 'QC', value: `${judged} of ${lines.length}`, done: lines.length > 0 && judged === lines.length },
            { label: 'Shipments', value: `${shipments.length}${loads.length ? ` (+${loads.length} suggested)` : ''}`, done: order.inProductionUnits === 0 && shipments.length > 0 },
            { label: 'Forwarder', value: shipments.length ? `${booked} of ${shipments.length}` : '—', done: shipments.length > 0 && booked === shipments.length },
            { label: 'Dispatched', value: shipments.length ? `${dispatched} of ${shipments.length}` : '—', done: shipments.length > 0 && dispatched === shipments.length },
        ];
    }, [order, loads.length]);


    const canIssue = order?.status === 'DRAFT';
    const isTerminal = order?.status === 'CANCELLED';
    const canAddShipment =
        !!order && order.status !== 'DRAFT' && order.status !== 'CANCELLED' && order.inProductionUnits > 0;
    const canCancel =
        !!order &&
        order.status !== 'CANCELLED' &&
        order.status !== 'SHIPPED' &&
        order.status !== 'RECEIVED' &&
        order.inTransitUnits === 0 &&
        order.receivedUnits === 0;

    return (
        <Modal opened={opened} onClose={onClose} size="90vw" centered withCloseButton={false} padding={0}>
            <div
                className="flex items-center justify-between"
                style={{ height: 52, padding: '0 20px', borderBottom: '1px solid rgb(226, 232, 240)' }}
            >
                <Group gap={12}>
                    <Text size="15px" fw={600} c={textColors.primary}>
                        {order ? `Order ${order.orderNumber}` : 'Order'}
                    </Text>
                    {order && <StatusBadge status={order.status} />}
                    {order && amendedSinceSent(order) && (
                        // Beside the status, because it is one: the vendor holds an older copy
                        // until Send to vendor again clears it.
                        <NeedChip
                            need={{
                                label: 'Changed since sent',
                                tone: 'warn',
                                title: 'A delivery added an over-delivery line after this order was last sent; the vendor holds the earlier copy.',
                            }}
                        />
                    )}
                    {order?.wmsPoCode && (
                        <Text size="12px" c={textColors.secondary} title="Created in the WMS (Unicommerce)">
                            WMS PO {order.wmsPoCode}
                        </Text>
                    )}
                </Group>
                <Group gap={8}>
                    {canIssue && !order?.wmsPoCode && (
                        // Once the order exists in the WMS its lines are the WMS's too; there is no
                        // update-PO push, so editing stops at that point (clone for changes).
                        <Button
                            size="xs"
                            variant="default"
                            onClick={() => order && onEdit(order)}
                            title="Edit this draft order."
                        >
                            Edit
                        </Button>
                    )}
                    {canIssue && (
                        <Button size="xs" variant="default" loading={busy === 'issue'}
                            onClick={() => order && run('issue', () => markMotherOrderIssued(order.id), 'Marked as issued')}>
                            Mark as issued
                        </Button>
                    )}
                    {order && (
                        // The document as it would go out now, for a look before any send - and
                        // the only way to see an amended order the way the vendor will.
                        <Button size="xs" variant="default" loading={busy === 'pdf'}
                            onClick={() => void run('pdf', () => openMotherOrderPdf(order.id), 'Opened in a new tab')}>
                            PDF
                        </Button>
                    )}
                    {order && !isTerminal && (
                        // Sending is how a draft is issued, and how an issued order reaches the
                        // vendor again after a receipt amended it. The chip says a resend is owed.
                        <>
                            {/*
                              * Never greyed out for want of an address: the dialog's To field
                              * takes one, pre-filled from the email book or the order when either
                              * knows it, and an issued order cannot be edited to add one anyway.
                              */}
                            <Button size="xs" loading={busy === 'send'} onClick={openSend}>
                                {order.sentAt ? 'Send to vendor again' : 'Send to vendor'}
                            </Button>
                        </>
                    )}
                    {wmsPlan.order && order && !order.wmsPoCode && !order.cancelledAt && (
                        // Any live order that is not yet in the WMS — Draft included, so a refused
                        // push (wrong vendor, say) can be corrected and retried from here.
                        <Button size="xs" variant="default" loading={busy === 'wms'}
                            title="Create this order as a purchase order in the WMS (Unicommerce). The order stays editable until this succeeds."
                            onClick={() => run('wms', () => pushOrderToWms(order.id), 'Purchase order created in WMS')}>
                            Push to WMS
                        </Button>
                    )}
                    {canAddShipment && (
                        <Button size="xs" onClick={() => onAddShipment()}>
                            + Add shipment
                        </Button>
                    )}
                    <Button size="xs" variant="default" loading={busy === 'clone'} disabled={!order}
                        onClick={() => order && run('clone', () => cloneMotherOrder(order.id), 'Cloned to a new draft')}>
                        Clone
                    </Button>
                    {canCancel && (
                        <Button size="xs" variant="default" color="red" loading={busy === 'cancel'}
                            onClick={() => order && run('cancel', () => cancelMotherOrder(order.id), 'Order cancelled')}>
                            Cancel
                        </Button>
                    )}
                    <button type="button" onClick={onClose}
                        style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: textColors.secondary }}>
                        ✕
                    </button>
                </Group>
            </div>

            <div style={{ padding: 32, maxHeight: '80vh', overflowY: 'auto' }}>
                {loading || !order ? (
                    <Text size="13px" c={textColors.muted} py={48} ta="center">
                        Loading order…
                    </Text>
                ) : (
                    <div className="space-y-6">
                        {/* Equal columns whatever each holds: a flex group let the two date pickers
                            claim more width than the text cells and the row read unevenly. */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))', gap: 16, alignItems: 'start' }}>
                            <div>
                                <FieldLabel>Vendor</FieldLabel>
                                <Text size="13px" c={textColors.primary}>{order.vendorName || '—'}</Text>
                            </div>
                            <div>
                                <FieldLabel>Order date</FieldLabel>
                                <Text size="13px" c={textColors.primary}>{formatDateDisplay(order.poDate)}</Text>
                            </div>
                            <div>
                                <FieldLabel>Expected ready</FieldLabel>
                                {isTerminal ? (
                                    <Text size="13px" c={order.expectedReadinessDate ? textColors.primary : textColors.muted}>
                                        {order.expectedReadinessDate ? formatDateDisplay(order.expectedReadinessDate) : '—'}
                                    </Text>
                                ) : (
                                    <DateSinglePicker
                                        value={order.expectedReadinessDate ?? ''}
                                        onChange={(v) =>
                                            run('expected-ready', () => updateOrderExpectedDates(order.id, { expectedReadinessDate: v || null }), 'Expected ready updated')
                                        }
                                        placeholder="Set a date"
                                        clearable
                                    />
                                )}
                            </div>
                            <div>
                                <FieldLabel>Expected delivery</FieldLabel>
                                {isTerminal ? (
                                    <Text size="13px" c={order.expectedDeliveryDate ? textColors.primary : textColors.muted}>
                                        {order.expectedDeliveryDate ? formatDateDisplay(order.expectedDeliveryDate) : '—'}
                                    </Text>
                                ) : (
                                    <DateSinglePicker
                                        value={order.expectedDeliveryDate ?? ''}
                                        onChange={(v) =>
                                            run('expected-delivery', () => updateOrderExpectedDates(order.id, { expectedDeliveryDate: v || null }), 'Expected delivery updated')
                                        }
                                        placeholder="Set a date"
                                        clearable
                                    />
                                )}
                            </div>
                            <div>
                                <FieldLabel>Total</FieldLabel>
                                <Text size="13px" c={textColors.primary}>{formatMoney(order.total, order.currency)}</Text>
                            </div>
                            {cbmEnabled && (
                                <div>
                                    <FieldLabel>Volume</FieldLabel>
                                    <Text size="13px" c={order.totalCbm == null ? textColors.muted : textColors.primary}>
                                        {order.totalCbm == null ? 'No CBM on file' : `${fmtCbm(order.totalCbm)} CBM`}
                                    </Text>
                                    {containersNeeded != null && order.maxCbmPerShipment && (
                                        <Text size="11px" c={textColors.secondary}>
                                            ≈ {containersNeeded} container{containersNeeded === 1 ? '' : 's'} at {order.maxCbmPerShipment} CBM
                                        </Text>
                                    )}
                                    {order.cbmMissingLines > 0 && (
                                        <Text size="11px" c="rgb(180, 83, 9)">
                                            {order.cbmMissingLines} line{order.cbmMissingLines === 1 ? '' : 's'} without CBM, not counted
                                        </Text>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={{ border: '1px solid rgb(226, 232, 240)', borderRadius: 8, padding: 16 }}>
                            <Group gap={8} align="baseline" mb={10}>
                                <Text
                                    size="11px"
                                    fw={700}
                                    c={textColors.secondary}
                                    style={{ letterSpacing: '0.06em' }}
                                >
                                    FULFILMENT
                                </Text>
                                <Text size="13px" c={textColors.secondary}>
                                    <span style={{ color: textColors.primary, fontWeight: 700 }}>
                                        {num(order.receivedUnits)}
                                    </span>{' '}
                                    of {num(order.orderedUnits)} units received
                                </Text>
                            </Group>
                            <FulfilmentBar order={order} />
                        </div>

                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                            {rail.map((step) => (
                                <span key={step.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: textColors.secondary }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: step.done ? 'rgb(45, 187, 136)' : 'rgb(203, 213, 225)' }} />
                                    {step.label}
                                    <span style={{ color: textColors.primary, fontWeight: 600 }}>{step.value}</span>
                                </span>
                            ))}
                        </div>

                        <div>
                            {linesEditable && (
                                <Group justify="space-between" mb={6} align="center">
                                    <Text size="11px" c={textColors.muted}>
                                        Ready on and QC are saved as you leave each cell. A line with no QC recorded is settled by its shipment.
                                    </Text>
                                    <Group gap={6} align="center">
                                        <Text size="11px" c={textColors.secondary}>Set ready date on all lines</Text>
                                        {everyReadyDateLocked ? (
                                            // Every line's date was set by a shipment; there is nothing left to apply it to.
                                            <Tooltip label={READY_DATE_LOCKED_HINT} withArrow>
                                                <Text size="11px" c={textColors.muted}>Set by shipment</Text>
                                            </Tooltip>
                                        ) : (
                                            <div style={{ width: 150 }}>
                                                <DateSinglePicker value="" onChange={(iso) => iso && void setAllReadiness(iso)} placeholder="Pick a date" />
                                            </div>
                                        )}
                                    </Group>
                                </Group>
                            )}
                            <DataSheet data={order.lines} columns={lineColumns} getRowKey={(r) => `${r.itemId}|${r.lineItemId}`} bare hideHeaderBar={false} rowHeight={48} />
                        </div>

                        {loads.length > 0 && canAddShipment && (
                            <div>
                                <SectionHeader>SUGGESTED LOADS</SectionHeader>
                                <Text size="11px" c={textColors.muted} mb={8}>
                                    Shippable lines grouped by ready date{cbmEnabled && order.maxCbmPerShipment ? `, packed to ${order.maxCbmPerShipment} CBM` : ''}. Create one to open the shipment form with these quantities filled in.
                                </Text>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                                    {loads.map((load) => {
                                        const capNum = cbmEnabled ? order.maxCbmPerShipment : null;
                                        const pct = capNum ? Math.min(100, (load.cbm / capNum) * 100) : 0;
                                        return (
                                            <div key={load.key} style={{ border: '1px solid rgb(226, 232, 240)', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                <Group justify="space-between" align="baseline">
                                                    <Text size="13px" fw={600} c={textColors.primary}>
                                                        {load.readyBy ? `Ready by ${formatDateDisplay(load.readyBy)}` : 'No ready date'}
                                                    </Text>
                                                    <Text size="11px" c={textColors.secondary}>
                                                        {Object.keys(load.qtys).length} line{Object.keys(load.qtys).length === 1 ? '' : 's'} · {num(load.units)} units
                                                    </Text>
                                                </Group>
                                                {capNum ? (
                                                    <>
                                                        <Progress value={pct} color={load.underFilled ? 'yellow' : 'blue'} size="sm" />
                                                        <Text size="11px" c={load.underFilled ? 'rgb(180, 83, 9)' : textColors.secondary}>
                                                            {load.nudge}
                                                            {load.uncounted > 0 ? ` · ${load.uncounted} line${load.uncounted === 1 ? '' : 's'} without CBM` : ''}
                                                        </Text>
                                                    </>
                                                ) : null}
                                                <Button
                                                    size="xs"
                                                    variant="light"
                                                    onClick={() => onAddShipment({ qtys: load.qtys, readinessDate: load.readyBy })}
                                                >
                                                    Create shipment
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div>
                            <SectionHeader>SHIPMENTS</SectionHeader>
                            {order.shipments.length === 0 ? (
                                <Text size="13px" c={textColors.muted}>
                                    No shipments yet. The full quantity is in production.
                                </Text>
                            ) : (
                                <ShipmentsTable
                                    shipments={order.shipments}
                                    onSelect={(s) => setSelectedShipmentId(s.poNumber)}
                                />
                            )}
                            <Text size="12px" c={textColors.muted} mt={6}>
                                Remaining in production: {num(order.inProductionUnits)} units
                            </Text>
                        </div>

                        {order.receipts.length > 0 && (
                            <div>
                                <SectionHeader>RECEIPTS</SectionHeader>
                                <ReceiptsTable receipts={order.receipts} lines={order.lines} />
                            </div>
                        )}

                        <div>
                            {order.paymentSchedule?.template?.length ? (
                                <Text size="12px" c={textColors.muted} mb={6}>
                                    Payment terms:{' '}
                                    <Text span fw={600} c={textColors.primary}>
                                        {order.paymentSchedule.termsName ?? 'Custom'}
                                    </Text>
                                    {order.paymentSchedule.termsName && order.paymentSchedule.termsCustomised
                                        ? ' (edited for this order)'
                                        : ''}
                                </Text>
                            ) : null}
                            <PaymentScheduleSection orderId={order.id} currency={order.currency} />
                        </div>
                    </div>
                )}
            </div>

            <ShipmentDetailPanel
                shipment={
                    selectedShipment && order
                        ? {
                              ...selectedShipment,
                              motherOrderId: order.id,
                              motherOrderNumber: order.orderNumber,
                              motherWmsPoCode: order.wmsPoCode,
                          }
                        : null
                }
                onClose={() => setSelectedShipmentId(null)}
                onChanged={onChanged}
            />

            {/*
              * Nested inside the order modal rather than beside it, the same way the shipment
              * panel above is: a sibling would lose the z-index fight with the overlay and
              * render greyed out behind it.
              */}
            <Modal
                opened={sendOpen}
                onClose={() => setSendOpen(false)}
                title="Send to vendor"
                size="md"
                centered
            >
                <Stack gap="md">
                    <Text size="sm" c={textColors.secondary}>
                        Order <strong>{order?.orderNumber}</strong> is about to be
                        emailed with its PDF attached.
                    </Text>
                    <TagsInput
                        label="To"
                        description="Type an address and press Enter. Pre-filled from Settings -> Email Settings."
                        placeholder="name@supplier.com"
                        value={sendTo}
                        onChange={setSendTo}
                    />
                    {order?.vendorEmail
                        && sendTo.length > 0
                        && sendTo[0] !== order.vendorEmail && (
                        <Checkbox
                            size="xs"
                            checked={updateOrderAddress}
                            onChange={(e) => setUpdateOrderAddress(e.currentTarget.checked)}
                            label={`Also correct the order's recorded contact (currently ${order.vendorEmail})`}
                            description="Updates the address printed on this order's PDF. Off by default."
                        />
                    )}
                    <TagsInput
                        label="CC"
                        description="Type an address and press Enter. Pre-filled from Settings -> Email Settings."
                        placeholder="ops@yourcompany.com"
                        value={sendCc}
                        onChange={setSendCc}
                    />
                    <Group justify="flex-end" gap="sm">
                        <Button
                            variant="default"
                            onClick={() => setSendOpen(false)}
                            disabled={busy === 'send'}
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={busy === 'send'}
                            disabled={sendTo.length === 0}
                            onClick={async () => {
                                if (!order) return;
                                setSendOpen(false);
                                await run(
                                    'send',
                                    () =>
                                        sendMotherOrder(order.id, {
                                            cc: sendCc,
                                            to: sendTo,
                                            updateOrderAddress,
                                        }),
                                    'Sent to vendor',
                                );
                            }}
                        >
                            Send
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Modal>
    );
};

// ─── Rich Create / Edit Order form (two-pane, live preview) ─────

function motherLineToDraft(line: MotherOrderLine, catalog: SkuOption[]): LineDraft {
    const matched = catalog.find(
        (o) =>
            (line.materialId && o.kind === 'material' && o.id === line.materialId) ||
            (line.productId && o.kind === 'product' && o.id === line.productId),
    );
    return {
        sku: matched?.sku ?? '',
        description: line.description ?? '',
        quantity: line.ordered,
        unitPrice: line.unitPrice ?? 0,
        gstRate: line.gstRate ?? '',
        materialId: line.materialId,
        productId: line.productId,
        expectedReadinessDate: line.expectedReadinessDate ?? '',
    };
}

const RichOrderFormModal: FC<{
    opened: boolean;
    existingOrder: MotherOrder | null;
    /** "Raise PO" from a work order: the new order starts with that material line and is pegged to it. */
    workOrderPrefill?: WorkOrderPrefill | null;
    onClose: () => void;
    vendors: VendorOption[];
    skuOptions: SkuOption[];
    onSaved: () => void;
}> = ({ opened, existingOrder, workOrderPrefill, onClose, vendors, skuOptions, onSaved }) => {
    const isEdit = !!existingOrder;
    const wmsPlan = useWmsPushPlan();
    const [pushToWms, setPushToWms] = useState(false);
    // Ticked by default whenever the policy pushes orders (PO_AT_ORDER); the operator opts out.
    useEffect(() => {
        if (opened && !isEdit) setPushToWms(!!wmsPlan.order);
    }, [opened, isEdit, wmsPlan.order]);
    const cbmEnabled = useCbmEnabled();
    const { showToastError, showToastSuccess } = useToaster();
    const [orderNumber, setOrderNumber] = useState('');
    /** Which stem the number was built from. Only ever the picker's own state. */
    const [numberPrefix, setNumberPrefix] = useState<string | null>(null);
    const [isAddingPrefix, setIsAddingPrefix] = useState(false);
    const queryClient = useQueryClient();
    const [poDate, setPoDate] = useState(todayIso());
    const [expiryDate, setExpiryDate] = useState('');
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
    const [vendorId, setVendorId] = useState<string | null>(null);
    const [vendorName, setVendorName] = useState('');
    const [vendorEmail, setVendorEmail] = useState('');
    const [vendorAddress, setVendorAddress] = useState('');
    const [vendorGstin, setVendorGstin] = useState('');
    const [billToName, setBillToName] = useState('');
    const [billToAddress, setBillToAddress] = useState('');
    const [billToGstin, setBillToGstin] = useState('');
    const [shipToName, setShipToName] = useState('');
    const [shipToEmail, setShipToEmail] = useState('');
    const [shipToAddress, setShipToAddress] = useState('');
    const [shipToGstin, setShipToGstin] = useState('');
    const [shipToStateCode, setShipToStateCode] = useState<string | null>(null);
    /** The Ship To destination picker's own value: 'wh:<id>' or 'vendor:<id>'. */
    const [shipToDestination, setShipToDestination] = useState<string | null>(null);
    /** True once somebody picks a state by hand, after which the GSTIN stops driving it. */
    const [placeOfSupplyPicked, setPlaceOfSupplyPicked] = useState(false);
    /** Held by hand or by the Bill To GSTIN; the Ship To side seeds the state only while false. */
    const placeOfSupplyLocked = usePlaceOfSupplyFromBillTo(billToGstin, placeOfSupplyPicked, setShipToStateCode);
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [currency, setCurrency] = useState('INR');
    const [taxRate, setTaxRate] = useState<number | ''>(0);
    const [notes, setNotes] = useState('');
    const [terms, setTerms] = useState('');
    const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
    const [charges, setCharges] = useState<ChargeDraft[]>([]);
    const [destinationCountry, setDestinationCountry] = useState('');
    const [payTerms, setPayTerms] = useState<PaymentTermsDraft>(emptyPaymentTermsDraft());
    const [saving, setSaving] = useState(false);
    /** Widens the picker past the vendor's mapped items. Off again whenever the vendor changes. */
    const [showAllItems, setShowAllItems] = useState(false);

    useEffect(() => {
        if (!opened) return;
        setSaving(false);
        setShowAllItems(false);
        if (existingOrder) {
            const o = existingOrder;
            setOrderNumber(o.orderNumber);
            setPoDate(o.poDate || todayIso());
            setExpiryDate(o.expiryDate ?? '');
            setExpectedDeliveryDate(o.expectedDeliveryDate ?? '');
            setVendorId(o.vendorId);
            setVendorName(o.vendorName ?? '');
            setVendorEmail(o.vendorEmail ?? '');
            setVendorAddress(o.vendorAddress ?? '');
            setVendorGstin(o.vendorGstin ?? '');
            setBillToName(o.billToName ?? '');
            setBillToAddress(o.billToAddress ?? '');
            setBillToGstin(o.billToGstin ?? '');
            setShipToName(o.shipToName ?? '');
            setShipToEmail(o.shipToEmail ?? '');
            setShipToAddress(o.shipToAddress ?? '');
            setShipToGstin(o.shipToGstin ?? '');
            setShipToStateCode(o.shipToStateCode ?? null);
            setShipToDestination(null);
            // An order already on file states its own place of supply; the GSTIN must not move it.
            setPlaceOfSupplyPicked(true);
            setContactName(o.contactName ?? '');
            setContactEmail(o.contactEmail ?? '');
            setCurrency(o.currency || 'INR');
            setTaxRate(Number(o.taxRate) || 0);
            setNotes(o.notes ?? '');
            setTerms(o.termsAndConditions ?? '');
            const hydrated = (o.lines ?? []).map((l) => motherLineToDraft(l, skuOptions));
            setLines(hydrated.length ? hydrated : [emptyLine()]);
            setCharges((o.additionalCharges ?? []).map((c) => ({ label: c.label, amount: Number(c.amount) || 0 })));
            setDestinationCountry(o.destinationCountry ?? '');
            setPayTerms(draftFromSchedule(o.paymentSchedule));
        } else {
            setOrderNumber('');
            setPoDate(todayIso());
            setExpiryDate('');
            setExpectedDeliveryDate('');
            setVendorId(null);
            setVendorName('');
            setVendorEmail('');
            setVendorAddress('');
            setVendorGstin('');
            setBillToName('');
            setBillToAddress('');
            setBillToGstin('');
            setShipToName('');
            setShipToEmail('');
            setShipToAddress('');
            setShipToGstin('');
            setShipToStateCode(null);
            setShipToDestination(null);
            setPlaceOfSupplyPicked(false);
            setContactName('');
            setContactEmail('');
            setCurrency('INR');
            setTaxRate(0);
            setNotes('');
            setTerms('');
            setLines([workOrderPrefill ? workOrderPrefillLine(workOrderPrefill, skuOptions) : emptyLine()]);
            setCharges([]);
            setDestinationCountry('');
            setPayTerms(emptyPaymentTermsDraft());
        }
    }, [opened, existingOrder, skuOptions, workOrderPrefill]);

    /**
     * The brand's standard terms, seeded into a NEW order only.
     *
     * AN EXISTING ORDER KEEPS ITS OWN, whatever they are — including none. Most orders on file
     * predate this setting and have no terms at all, and filling those in on open would show a
     * document terms it was never sent with.
     *
     * Seeded once per opening, and only into an untouched box, so clearing them for a one-off
     * order sticks rather than being undone by the next render.
     */
    const { data: orgForTerms } = useQuery(
        ['org-config', getOrganizationIdFromStorage()],
        () => getOrgConfig(getOrganizationIdFromStorage() as string),
        { enabled: !!getOrganizationIdFromStorage() },
    );

    /**
     * The brand's purchase-order stems, off the org config already fetched for the terms.
     * Absent for a brand that has never opened Settings > Order Numbering, and the picker
     * hides itself - the field stays the single "Auto-generated if blank" box.
     */
    const poPrefixes = useMemo(
        () =>
            (orgForTerms?.documentNumbering?.PURCHASE_ORDER?.prefixes ?? []).filter((entry) =>
                entry.value?.trim(),
            ),
        [orgForTerms],
    );

    /**
     * Picking a stem fills the number in - the server works out what comes next, because the
     * client cannot see the orders somebody else raised this morning. A failure leaves the
     * prefix alone in the box: still a usable start, completed at save.
     */
    const applyPoPrefix = (prefix: string | null) => {
        setNumberPrefix(prefix);
        if (!prefix) return;

        const padWidth = orgForTerms?.documentNumbering?.PURCHASE_ORDER?.padWidth;
        void peekNextPoNumber(prefix, padWidth)
            .then((next) => setOrderNumber(next || prefix))
            .catch(() => setOrderNumber(prefix));
    };

    /**
     * A GSTIN carries its state in the first two digits, so typing one settles the place of
     * supply, but only while nothing stronger holds it: a state chosen by hand, or the Bill To
     * GSTIN, which names the place of supply whenever there is one (usePlaceOfSupplyFromBillTo).
     */
    const handleShipToGstin = (value: string) => {
        setShipToGstin(value);
        if (placeOfSupplyLocked) return;

        const derived = placeOfSupplyFromGstin(value);
        if (derived) setShipToStateCode(derived);
    };

    // The production form's warehouse picker, reused verbatim: INTERNAL warehouses with
    // their addresses on board.
    const { data: warehousePicks } = useQuery(
        ['warehouse-picker'],
        listWarehousesForPicker,
    );

    const shipToDestinationData = useMemo(
        () => [
            {
                group: 'Your warehouses',
                items: (warehousePicks ?? []).map((w) => ({
                    value: `wh:${w.warehouseId}`,
                    label: w.name || w.warehouseId,
                })),
            },
            {
                group: 'Manufacturers',
                items: vendors.map((v) => ({
                    value: `vendor:${v.vendorId}`,
                    label: v.name || v.vendorId,
                })),
            },
        ],
        [warehousePicks, vendors],
    );

    /**
     * Picking a destination fills Ship To from the master record - a warehouse row or the
     * vendor master - the same contract as the production form's picker: seeded fields are
     * set unconditionally (switching to an address-less destination must not keep the
     * previous one's address standing), clearing clears what was seeded, and a hand-picked
     * place of supply always survives.
     */
    const handleShipToDestination = async (value: string | null) => {
        setShipToDestination(value);

        if (!value) {
            setShipToName('');
            setShipToEmail('');
            setShipToAddress('');
            setShipToGstin('');
            if (!placeOfSupplyLocked) setShipToStateCode(null);
            return;
        }

        if (value.startsWith('wh:')) {
            const warehouse = warehousePicks?.find(
                (w) => `wh:${w.warehouseId}` === value,
            );
            if (!warehouse) return;
            setShipToName(warehouse.name || warehouse.warehouseId);
            setShipToEmail('');
            setShipToAddress(
                [warehouse.street, warehouse.city, warehouse.zipCode, warehouse.country]
                    .map((part) => part?.trim())
                    .filter(Boolean)
                    .join(', '),
            );
            // A warehouse row carries no GSTIN; its state names the place of supply instead.
            setShipToGstin('');
            if (!placeOfSupplyLocked) {
                setShipToStateCode(gstStateCodeFromStateName(warehouse.state));
            }
            return;
        }

        const pickedVendorId = value.slice('vendor:'.length);
        try {
            const v = await getVendor(pickedVendorId);
            setShipToName(v.legalName?.trim() || v.name || pickedVendorId);
            setShipToEmail(v.email ?? '');
            setShipToAddress(
                [v.street, v.city, v.zip, v.country]
                    .map((part) => part?.trim())
                    .filter(Boolean)
                    .join(', '),
            );
            setShipToGstin(v.taxCode ?? '');
            if (!placeOfSupplyLocked) {
                setShipToStateCode(placeOfSupplyFromGstin(v.taxCode));
            }
        } catch {
            /* vendor detail unavailable - fields stay editable */
        }
    };

    const seededTerms = useRef(false);
    useEffect(() => {
        if (!opened) {
            seededTerms.current = false;
            return;
        }
        if (existingOrder || seededTerms.current || !orgForTerms) return;
        seededTerms.current = true;
        setTerms((current) => current || (orgForTerms.documentTerms ?? ''));
    }, [opened, existingOrder, orgForTerms]);

    /**
     * Bill To is this brand billing itself, so it is the same three values on every order.
     * Seeded on the same terms as the standard terms above - once per opening, only into
     * untouched boxes, and never over an existing order, which states its own parties.
     * `legalName` is the registered entity and `name` the trading one; the document wants
     * the first and falls back to the second.
     */
    const seededBillTo = useRef(false);
    useEffect(() => {
        if (!opened) {
            seededBillTo.current = false;
            return;
        }
        if (existingOrder || seededBillTo.current || !orgForTerms) return;
        seededBillTo.current = true;
        setBillToName((current) => current || orgForTerms.legalName || orgForTerms.name || '');
        setBillToAddress((current) => current || (orgForTerms.address ?? ''));
        setBillToGstin((current) => current || (orgForTerms.gstin ?? ''));
    }, [opened, existingOrder, orgForTerms]);

    const subtotal = useMemo(
        () => lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0),
        [lines],
    );
    /** Units on the order, for terms that carry a minimum in units rather than value. */
    const orderUnits = useMemo(() => lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0), [lines]);
    /*
     * Per-line rates win the moment any line states one - same rule as the backend's
     * computeOrderTotals, so the form's figures match what the save comes back with.
     */
    const perLineTax = lines.reduce(
        (sum, l) =>
            l.gstRate === '' || l.gstRate == null
                ? sum
                : sum
                  + ((Number(l.quantity) || 0)
                      * (Number(l.unitPrice) || 0)
                      * Number(l.gstRate))
                      / 100,
        0,
    );
    const hasLineRates = lines.some((l) => l.gstRate !== '' && l.gstRate != null);
    const taxAmount = hasLineRates
        ? Math.round(perLineTax * 100) / 100
        : (subtotal * (Number(taxRate) || 0)) / 100;
    const chargesTotal = useMemo(() => charges.reduce((s, c) => s + (Number(c.amount) || 0), 0), [charges]);
    const total = subtotal + taxAmount + chargesTotal;

    /**
     * What this vendor has been mapped to supply, both halves of the catalogue.
     *
     * MAPPED FIRST, NOT MAPPED ONLY — the production-order picker's rule, for the same reason.
     * The mapping tables are a partial record of who supplies what: a quarter of the lines
     * already placed with Mokobara's Bange are on products mapped to somebody else or to
     * nobody, and 26 of that brand's 43 suppliers have no mappings at all. A hard filter would
     * make those vendors impossible to order from. So the mapped list is the default, the
     * checkbox is the way past it, and a vendor with nothing mapped gets the full catalogue.
     *
     * The product half shares its cache key with the production-order modal.
     */
    const vendorProductsQuery = useQuery(
        ['vendor-products', vendorId],
        () => listProductsForVendor(vendorId as string),
        { enabled: opened && !!vendorId },
    );
    const vendorMaterialsQuery = useQuery(
        ['vendor-materials', vendorId],
        () => listMaterialsForVendor(vendorId as string),
        { enabled: opened && !!vendorId },
    );

    /**
     * The agreed rate per picker value, for filling a picked line. Only mapped items the
     * catalogue actually offers are in here, so its size is what the checkbox label counts.
     * A null currency is the brand's own.
     */
    const vendorTerms = useMemo(() => {
        const byProductId = new Map((vendorProductsQuery.data ?? []).map((p) => [p.productId, p]));
        const byMaterialId = new Map((vendorMaterialsQuery.data ?? []).map((m) => [m.materialId, m]));
        const bySku = new Map<string, { price: number | null; currency: string | null }>();
        for (const o of skuOptions) {
            if (o.kind === 'product') {
                const p = byProductId.get(o.id);
                if (p) bySku.set(o.sku, { price: p.price, currency: p.currency });
            } else {
                const m = byMaterialId.get(o.id);
                if (m) bySku.set(o.sku, { price: m.cost, currency: m.currency });
            }
        }
        return bySku;
    }, [skuOptions, vendorProductsQuery.data, vendorMaterialsQuery.data]);
    const mappedCount = vendorTerms.size;
    const vendorTermsLoading = vendorProductsQuery.isFetching || vendorMaterialsQuery.isFetching;
    const narrowed = !!vendorId && !showAllItems && mappedCount > 0;

    /**
     * What the picker and the CSV import offer. Lines already on the form stay selectable
     * either way — an order on file, or a line picked before the vendor changed, must never
     * come back with a blank SKU.
     */
    const pickerOptions = useMemo(() => {
        if (!narrowed) return skuOptions;
        const picked = new Set(lines.map((l) => l.sku).filter(Boolean));
        return skuOptions.filter((o) => vendorTerms.has(o.sku) || picked.has(o.sku));
    }, [narrowed, skuOptions, vendorTerms, lines]);

    /** SKUs already on the order: greyed in the dropdown, so a second line cannot be opened. */
    const pickedSkus = useMemo(() => new Set(lines.map((l) => l.sku).filter(Boolean)), [lines]);
    const skuData = useMemo(
        // Name first, then the code a person quotes (the catalog id, not the barcode-ish
        // sku) - mirroring the production order picker.
        () =>
            pickerOptions.map((o) => ({
                value: o.sku,
                label: `${o.name} — ${o.id}`,
                disabled: pickedSkus.has(o.sku),
            })),
        [pickerOptions, pickedSkus],
    );

    /**
     * VOLUME, for brands measured by it. The container cap is the highest across the vendor's
     * lanes: an order has no destination yet, and most vendors ship on one kind of container
     * anyway. The shipment created later measures against its own lane. Ship To plays no part.
     */
    const { data: vendorLanes } = useQuery(
        ['vendor-lanes', vendorId],
        () => getVendorLanes(vendorId as string),
        { enabled: opened && cbmEnabled && !!vendorId },
    );
    const orderCap = useMemo(() => {
        const caps = (vendorLanes ?? [])
            .map((l) => l.maxCbmPerShipment)
            .filter((c): c is number => c != null && c > 0);
        return caps.length ? Math.max(...caps) : null;
    }, [vendorLanes]);
    const unitCbmBySku = useMemo(
        () => new Map(skuOptions.filter((o) => o.kind === 'product').map((o) => [o.sku, o.volumeCbm ?? null])),
        [skuOptions],
    );
    const cbmOf = (line: LineDraft): number | null => {
        if (!line.sku || line.materialId) return null;
        const unit = unitCbmBySku.get(line.sku);
        return unit == null || unit <= 0 ? null : (Number(line.quantity) || 0) * unit;
    };
    const volume = useMemo(() => {
        let sum = 0;
        let counted = 0;
        const missing: string[] = [];
        for (const l of lines) {
            if (!l.sku || l.materialId) continue;
            const unit = unitCbmBySku.get(l.sku);
            if (unit == null || unit <= 0) {
                missing.push(l.description || l.sku);
                continue;
            }
            sum += (Number(l.quantity) || 0) * unit;
            counted += 1;
        }
        const containers =
            orderCap && counted > 0
                ? packIntoContainers(
                      lines.map((l) => ({
                          key: l.sku,
                          name: l.description,
                          quantity: Number(l.quantity) || 0,
                          unitCbm: l.materialId ? null : (unitCbmBySku.get(l.sku) ?? null),
                      })),
                      orderCap,
                  )
                : null;
        return { total: counted > 0 ? sum : null, missing, containers };
    }, [lines, unitCbmBySku, orderCap]);

    const handleVendor = async (id: string | null) => {
        setVendorId(id);
        setShowAllItems(false);
        // Terms belong to the vendor they were agreed with. `touched` on an existing order so
        // the new vendor's terms are saved over the old vendor's rather than shown and dropped.
        if (id !== vendorId) setPayTerms({ ...emptyPaymentTermsDraft(), touched: isEdit });
        if (!id) return;
        try {
            const v = await ReparoApi<{ name?: string; email?: string; street?: string; city?: string; country?: string; zip?: string; taxCode?: string; poNumberPrefix?: string | null }>({
                method: 'GET',
                url: `/vendors/${id}`,
            });
            if (v) {
                setVendorName(v.name ?? '');
                setVendorEmail(v.email ?? '');
                setVendorAddress([v.street, v.city, v.country, v.zip].filter(Boolean).join(', '));
                setVendorGstin(v.taxCode ?? '');
                // A NEW ORDER TAKES THE VENDOR'S PREFIX, when the vendor has one and it is
                // still one of the brand's stems (a stem deleted in Settings is not revived
                // here). An order on file keeps its number; a vendor with none leaves the
                // picker as it is.
                const vendorPrefix = v.poNumberPrefix?.trim();
                const stem = vendorPrefix
                    ? poPrefixes.find((entry) => entry.value.trim() === vendorPrefix)?.value.trim()
                    : undefined;
                if (!isEdit && stem) applyPoPrefix(stem);
            }
        } catch {
            /* vendor detail unavailable — fields stay editable */
        }

        // A NEW ORDER FOLLOWS THE VENDOR'S CURRENCY. Mokobara's import suppliers are mapped in
        // USD while the form opens in INR; seeding a dollar rate onto a rupee order would print
        // a wrong document, and refusing to seed would leave the agreed rate unused. So when
        // the mapped terms carry one currency and nothing has been priced yet, the order adopts
        // it. An order on file, or one with a rate already typed, keeps what it has. Product and
        // material terms both count (The Stack buys fish oil from Epax in USD). Shares the
        // pickers' cache entries, so this costs no extra request once the lists are in.
        if (!isEdit && !lines.some((l) => l.unitPrice !== '')) {
            try {
                const [products, materials] = await Promise.all([
                    queryClient.fetchQuery(['vendor-products', id], () => listProductsForVendor(id)),
                    queryClient.fetchQuery(['vendor-materials', id], () => listMaterialsForVendor(id)),
                ]);
                const currencies = new Set(
                    [...products, ...materials].map((t) => t.currency).filter((c): c is string => !!c),
                );
                if (currencies.size === 1) setCurrency([...currencies][0]);
            } catch {
                /* terms unavailable — the currency stays as picked */
            }
        }
    };

    /**
     * "Raise PO" from a work order: a material only one supplier is mapped to gets that supplier,
     * picked exactly as a click would (details, prefix, currency, terms, agreed price). Once per
     * opening, never over a vendor already chosen; none or several leaves the choice open.
     */
    const seededVendor = useRef(false);
    useEffect(() => {
        if (!opened) {
            seededVendor.current = false;
            return;
        }
        // Waits for the supplier list: the vendor must be one the picker can show.
        if (isEdit || !workOrderPrefill || vendorId || seededVendor.current || vendors.length === 0) return;
        seededVendor.current = true;
        void listVendorIdsForMaterial(workOrderPrefill.materialId)
            .then((ids) => {
                if (ids.length === 1 && vendors.some((v) => v.vendorId === ids[0])) void handleVendor(ids[0]);
            })
            .catch(() => {
                /* mappings unavailable - the vendor is picked by hand */
            });
        // handleVendor is a fresh closure every render; this runs on the opening alone.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, isEdit, workOrderPrefill, vendorId, vendors]);

    const updateLine = (idx: number, patch: Partial<LineDraft>) =>
        setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

    const onSkuPicked = (idx: number, sku: string) => {
        // One line per SKU. The dropdown already greys a picked SKU; this catches every other
        // path onto the line and points at where the quantity actually lives.
        const already = lines.findIndex((l, i) => i !== idx && l.sku === sku);
        if (already !== -1) {
            showToastError({
                title: 'Already on this order',
                subtitle: `Edit the quantity on line ${already + 1} instead.`,
            });
            return;
        }
        const found = skuOptions.find((o) => o.sku === sku);
        const agreed = vendorTerms.get(sku);
        const price = agreed?.price ?? null;
        // The agreed rate is a suggestion, and only where a figure exists in the order's own
        // currency: a mapping without a price must not fill in a zero somebody would then have
        // to notice and clear, and a dollar rate must not land on a rupee order.
        const seedPrice = price != null && (agreed?.currency == null || agreed.currency === currency);
        updateLine(idx, {
            sku,
            description: found?.name ?? '',
            materialId: found?.kind === 'material' ? found.id : null,
            productId: found?.kind === 'product' ? found.id : null,
            // Seeded from the catalog, editable after: the slab is the product's fact,
            // and '' stays '' when the catalog has no rate - never a presumed 0%.
            gstRate: found?.gstRate ?? '',
            ...(seedPrice ? { unitPrice: price } : {}),
            // Said on the line, so a typed rate is known to be typed rather than confirmed.
            // Only once the vendor's prices are in; while they load nothing is claimed.
            noAgreedPrice: !vendorTermsLoading && !!sku && price == null,
            // From here the price follows the line's quantity and the order's terms and
            // currency (useAgreedLinePrices below), until somebody types one.
            priceAuto: true,
            priceNote: undefined,
            priceWarn: false,
            priceDetail: undefined,
        });
    };

    /*
     * THE PRICE FOLLOWS THE AGREEMENT. A line the form priced is re-priced from the vendor's
     * agreed prices whenever its quantity (tiers), the order's payment terms option or its
     * currency changes. A price somebody typed, or one loaded from a saved order or a CSV, is
     * left alone. The answer is applied only to a line that still holds the same item and
     * quantity it was asked for.
     */
    const autoPricedLines = lines.flatMap((l) =>
        l.priceAuto && l.sku && (l.productId || l.materialId)
            ? [{
                  key: l.sku,
                  itemType: (l.productId ? 'product' : 'material') as 'product' | 'material',
                  itemId: (l.productId ?? l.materialId) as string,
                  quantity: l.quantity === '' ? undefined : Number(l.quantity),
              }]
            : [],
    );
    useAgreedLinePrices({
        enabled: opened,
        vendorId,
        currency,
        termsOptionId: payTerms.termsOptionId,
        lines: autoPricedLines,
        onResolved: (results) => {
            const bySku = new Map(results.map((r) => [r.key, r]));
            setLines((prev) =>
                prev.map((l) => {
                    const r = bySku.get(l.sku);
                    const quantity = l.quantity === '' ? undefined : Number(l.quantity);
                    if (!r || !l.priceAuto || r.quantity !== quantity) return l;
                    return { ...l, ...r.patch };
                }),
            );
        },
    });

    const updateCharge = (idx: number, patch: Partial<ChargeDraft>) =>
        setCharges((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

    const handleSave = async () => {
        const items = lines
            .filter((l) => (l.materialId || l.productId) && Number(l.quantity) > 0)
            .map((l) => ({
                description: l.description || l.sku,
                materialId: l.materialId,
                productId: l.productId,
                quantity: Number(l.quantity) || 0,
                unitPrice: Number(l.unitPrice) || 0,
                gstRate: l.gstRate === '' ? null : Number(l.gstRate),
                expectedReadinessDate: l.expectedReadinessDate || null,
            }));
        if (items.length === 0) {
            showToastError({ title: 'Add at least one line item' });
            return;
        }
        const additionalCharges = charges
            .filter((c) => c.label.trim())
            .map((c) => ({ label: c.label.trim(), amount: Number(c.amount) || 0 }));
        const paymentTerms = paymentTermsToPayload(payTerms);
        if (payTerms.choiceRequired && payTerms.tranches.length === 0) {
            showToastError({ title: 'Pick payment terms', subtitle: `${vendorName || 'This vendor'} offers more than one set of terms. Choose the one this order is placed on.` });
            return;
        }
        if (payTerms.saveToVendorDefault && !payTerms.saveAsOptionName.trim()) {
            showToastError({ title: 'Name the terms you are saving', subtitle: 'They are kept on the vendor under that name.' });
            return;
        }
        if (payTerms.tranches.length > 0 && !paymentTerms) {
            showToastError({ title: 'Payment terms are incomplete', subtitle: 'Every term needs a percentage, and they must sum to 100%.' });
            return;
        }
        if (paymentTerms && Math.abs(templateSum(payTerms) - 100) > 0.01) {
            showToastError({ title: 'Payment terms must sum to 100%' });
            return;
        }
        if (paymentTerms?.route === 'CREDIT_PARTNER' && !paymentTerms.creditPartnerId) {
            showToastError({ title: 'Pick a credit partner for the credit route' });
            return;
        }
        const shared = {
            poDate,
            expiryDate: expiryDate || undefined,
            expectedDeliveryDate: expectedDeliveryDate || null,
            vendorId: vendorId || undefined,
            vendorName: vendorName.trim() || undefined,
            vendorEmail: vendorEmail.trim() || undefined,
            vendorAddress: vendorAddress.trim() || undefined,
            vendorGstin: vendorGstin.trim() || undefined,
            billToName: billToName.trim() || undefined,
            billToAddress: billToAddress.trim() || undefined,
            billToGstin: billToGstin.trim() || undefined,
            shipToName: shipToName.trim() || undefined,
            shipToAddress: shipToAddress.trim() || undefined,
            shipToEmail: shipToEmail.trim() || undefined,
            shipToGstin: shipToGstin.trim() || undefined,
            shipToStateCode: shipToStateCode || undefined,
            // Destination warehouse resolves the lane and lead times on the backend;
            // a manufacturer destination is job-work and has no inbound lane.
            warehouseId: shipToDestination?.startsWith('wh:')
                ? shipToDestination.slice('wh:'.length)
                : undefined,
            contactName: contactName.trim() || undefined,
            contactEmail: contactEmail.trim() || undefined,
            currency,
            taxRate: Number(taxRate) || 0,
            notes: notes.trim() || undefined,
            termsAndConditions: terms.trim() || undefined,
            items,
            additionalCharges,
            destinationCountry: destinationCountry.trim() || undefined,
        };
        setSaving(true);
        try {
            if (isEdit && existingOrder) {
                await updateMotherOrder(existingOrder.id, shared);
                // Terms edits ride their own endpoint (freeze/lock semantics
                // live server-side); only sent when the user touched the card.
                if (payTerms.touched && paymentTerms) {
                    await updateOrderPaymentTerms(existingOrder.id, paymentTerms);
                }
            } else {
                const created = await createMotherOrder({
                    ...shared,
                    orderNumber: orderNumber.trim() || undefined,
                    paymentTerms,
                    workOrderId: workOrderPrefill?.workOrderId,
                } as CreateMotherOrderInput);
                showToastSuccess({ title: 'Order created' });
                // External action as a second step: the order is ours once saved, and a
                // WMS refusal must not read as "order failed".
                if (pushToWms && wmsPlan.order) {
                    try {
                        await pushOrderToWms(created.id);
                        showToastSuccess({ title: 'Purchase order created in WMS' });
                    } catch (e: any) {
                        showToastError({
                            title: 'Order saved, but the WMS push failed',
                            subtitle: errMessage(e, 'Retry from the order with "Push to WMS".'),
                        });
                    }
                }
                onSaved();
                onClose();
                return;
            }
            showToastSuccess({ title: 'Order saved' });
            onSaved();
            onClose();
        } catch (e: any) {
            showToastError({ title: 'Could not save order', subtitle: errMessage(e, 'Please retry.') });
        } finally {
            setSaving(false);
        }
    };

    const previewProps: PreviewProps = {
        poNumber: orderNumber,
        poDate,
        expiryDate,
        status: existingOrder?.status ?? 'DRAFT',
        currency,
        vendorName,
        vendorEmail,
        vendorAddress,
        vendorGstin,
        billToName,
        billToAddress,
        billToGstin,
        shipToName,
        shipToEmail,
        shipToAddress,
        shipToGstin,
        shipToStateCode,
        contactName,
        contactEmail,
        lines,
        taxRate: Number(taxRate) || 0,
        subtotal,
        taxAmount,
        charges: charges.map((c) => ({ label: c.label, amount: Number(c.amount) || 0 })),
        total,
        notes,
        terms,
    };

    return (
        <>
        <DocumentAuthoringShell
            opened={opened}
            onClose={onClose}
            title={isEdit ? `Edit Order ${existingOrder?.orderNumber ?? ''}` : 'Create Purchase Order'}
            preview={<DocumentPreview {...previewProps} />}
            actions={
                <>
                    {!isEdit && wmsPlan.order && (
                        <Checkbox
                            label="Also create in WMS"
                            description="External action: creates this order as a purchase order in Unicommerce."
                            checked={pushToWms}
                            onChange={(e) => setPushToWms(e.currentTarget.checked)}
                            mr="auto"
                        />
                    )}
                    <Button variant="default" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} loading={saving}>
                        {isEdit ? 'Save changes' : 'Create Purchase Order'}
                    </Button>
                </>
            }
            form={
                <>
                        {workOrderPrefill && !isEdit && <WorkOrderPrefillBanner prefill={workOrderPrefill} />}
                        <SectionHeader>DETAILS</SectionHeader>
                        <div className="space-y-3">
                            <Group grow>
                                <div>
                                    {/*
                                      * The stem and the number, when the brand has defined
                                      * stems - CREATE ONLY. In edit the number is the order's
                                      * identity and stays a read-only box.
                                      */}
                                    <Group justify="space-between" align="baseline" gap={8}>
                                        <FieldLabel>Order Number</FieldLabel>
                                        {!isEdit && (
                                            <button
                                                type="button"
                                                className={woClasses.createLink}
                                                onClick={() => setIsAddingPrefix(true)}
                                            >
                                                + Prefix
                                            </button>
                                        )}
                                    </Group>
                                    {!isEdit ? (
                                        <Group gap={6} wrap="nowrap" align="flex-start">
                                            <Select
                                                placeholder="Prefix"
                                                data={[
                                                    ...poPrefixes.map((entry) => entry.value.trim()),
                                                    { value: '__add_prefix__', label: '+ Add prefix' },
                                                ]}
                                                value={numberPrefix}
                                                onChange={(picked) => {
                                                    if (picked === '__add_prefix__') {
                                                        setIsAddingPrefix(true);
                                                        return;
                                                    }
                                                    applyPoPrefix(picked);
                                                }}
                                                clearable
                                                comboboxProps={{ width: 220, position: 'bottom-start' }}
                                                w={150}
                                            />
                                            <TextInput
                                                placeholder="Auto-generated if blank"
                                                value={orderNumber}
                                                onChange={(e) => setOrderNumber(e.currentTarget.value)}
                                                flex={1}
                                            />
                                        </Group>
                                    ) : (
                                        <TextInput placeholder="Auto-generated if blank" value={orderNumber}
                                            onChange={(e) => setOrderNumber(e.currentTarget.value)} readOnly={isEdit} disabled={isEdit} />
                                    )}
                                </div>
                                <div>
                                    <FieldLabel>Currency</FieldLabel>
                                    <Select
                                        data={CURRENCY_OPTIONS}
                                        value={currency}
                                        onChange={(v) => setCurrency(v ?? 'INR')}
                                        allowDeselect={false}
                                        searchable
                                        comboboxProps={{ width: 180, withinPortal: true, position: 'bottom-start' }}
                                    />
                                </div>
                            </Group>
                            <Group grow>
                                <div>
                                    <FieldLabel>Issue Date</FieldLabel>
                                    <DateSinglePicker value={poDate} onChange={(v) => setPoDate(v || todayIso())} placeholder="Pick a date" />
                                </div>
                                <div>
                                    <FieldLabel>Expiry Date</FieldLabel>
                                    <DateSinglePicker value={expiryDate} onChange={(v) => setExpiryDate(v || '')} placeholder="Pick a date" clearable />
                                </div>
                                <div>
                                    <FieldLabel>Expected Delivery</FieldLabel>
                                    <DateSinglePicker value={expectedDeliveryDate} onChange={(v) => setExpectedDeliveryDate(v || '')} placeholder="Pick a date" clearable />
                                </div>
                            </Group>
                            <Group grow>
                                <div>
                                    <FieldLabel>Destination Country</FieldLabel>
                                    <TextInput placeholder='e.g. "India", "UAE" — resolves the vendor payment terms' value={destinationCountry}
                                        onChange={(e) => setDestinationCountry(e.currentTarget.value)} />
                                </div>
                            </Group>
                        </div>

                        <SectionHeader>VENDOR</SectionHeader>
                        <div className="space-y-2">
                            <Select placeholder="Search and select a vendor" data={vendors.map((v) => ({ value: v.vendorId, label: v.name || v.vendorId }))}
                                value={vendorId} onChange={handleVendor} searchable clearable nothingFoundMessage="No vendors found"
                                comboboxProps={{ position: 'bottom-start' }} />
                            <TextInput placeholder="Vendor name" value={vendorName} onChange={(e) => setVendorName(e.currentTarget.value)} />
                            <TextInput placeholder="Email" value={vendorEmail} onChange={(e) => setVendorEmail(e.currentTarget.value)} />
                            <TextInput placeholder="Address" value={vendorAddress} onChange={(e) => setVendorAddress(e.currentTarget.value)} />
                            <TextInput placeholder="GST number" value={vendorGstin} onChange={(e) => setVendorGstin(e.currentTarget.value)} />
                        </div>

                        <SectionHeader>BILL TO</SectionHeader>
                        <div className="space-y-2">
                            <TextInput placeholder="Business name" value={billToName} onChange={(e) => setBillToName(e.currentTarget.value)} />
                            <Textarea placeholder="Address" value={billToAddress} onChange={(e) => setBillToAddress(e.currentTarget.value)} autosize minRows={1} />
                            <TextInput placeholder="GST number" value={billToGstin} onChange={(e) => setBillToGstin(e.currentTarget.value)} />
                        </div>

                        <SectionHeader>SHIP TO</SectionHeader>
                        <div className="space-y-2">
                            <Select
                                placeholder="Pick a destination — your warehouse or a manufacturer"
                                data={shipToDestinationData}
                                value={shipToDestination}
                                onChange={(value) => {
                                    void handleShipToDestination(value);
                                }}
                                searchable
                                clearable
                                nothingFoundMessage="No matching destination"
                                comboboxProps={{ position: 'bottom-start' }}
                            />
                            <TextInput placeholder="Name" value={shipToName} onChange={(e) => setShipToName(e.currentTarget.value)} />
                            <TextInput placeholder="Email" value={shipToEmail} onChange={(e) => setShipToEmail(e.currentTarget.value)} />
                            <Textarea placeholder="Address" value={shipToAddress} onChange={(e) => setShipToAddress(e.currentTarget.value)} autosize minRows={1} />
                            <TextInput placeholder="GST number" value={shipToGstin} onChange={(e) => handleShipToGstin(e.currentTarget.value)} />
                            <Select
                                placeholder="Place of supply (state) — sets IGST vs CGST+SGST"
                                data={GST_STATE_OPTIONS}
                                value={shipToStateCode}
                                onChange={(value) => {
                                    setPlaceOfSupplyPicked(true);
                                    setShipToStateCode(value);
                                }}
                                searchable
                                clearable
                                nothingFoundMessage="No matching state"
                                comboboxProps={{ position: 'bottom-start' }}
                            />
                        </div>

                        <SectionHeader>CONTACT DETAIL</SectionHeader>
                        <div className="space-y-2">
                            <TextInput placeholder="Name" value={contactName} onChange={(e) => setContactName(e.currentTarget.value)} />
                            <TextInput placeholder="Email" value={contactEmail} onChange={(e) => setContactEmail(e.currentTarget.value)} />
                        </div>

                        <SectionHeader>LINE ITEMS</SectionHeader>
                        {vendorId && mappedCount > 0 && (
                            <Checkbox
                                size="xs"
                                mb={8}
                                checked={showAllItems}
                                onChange={(e) => setShowAllItems(e.currentTarget.checked)}
                                label={`Show all items (${mappedCount} mapped to ${vendorName || 'this vendor'})`}
                            />
                        )}
                        {vendorId && mappedCount === 0 && !vendorTermsLoading && (
                            <Text size="11px" c="dimmed" mb={8}>
                                No items mapped to this vendor — showing the full catalogue.
                            </Text>
                        )}
                        <LineItemsTable
                            lines={lines}
                            skuData={skuData}
                            currency={currency}
                            onUpdateLine={updateLine}
                            onSkuPicked={onSkuPicked}
                            onRemoveLine={(idx) => setLines((prev) => prev.filter((_, i) => i !== idx))}
                            onAddLine={() => setLines((prev) => [...prev, emptyLine()])}
                            onClearAll={() => setLines([emptyLine()])}
                            skuOptions={pickerOptions}
                            nothingFoundMessage={
                                narrowed
                                    ? `Not mapped to ${vendorName || 'this vendor'} — tick "Show all items" for the full catalogue`
                                    : undefined
                            }
                            csvUnmatchedReason={narrowed ? `not mapped to ${vendorName || 'this vendor'}` : undefined}
                            cbmOf={cbmEnabled ? cbmOf : undefined}
                            readiness
                            onImportLines={(imported) => {
                                const { lines: next, mergedRows } = mergeImportedLines(lines, imported);
                                setLines(next);
                                if (mergedRows > 0) {
                                    showToastSuccess({
                                        title: `${mergedRows} row${mergedRows === 1 ? '' : 's'} added to lines already on the order`,
                                    });
                                }
                            }}
                        />

                        {cbmEnabled && (
                            <div style={{ marginTop: 12, border: '1px solid rgb(226, 232, 240)', borderRadius: 6, padding: '12px 14px', background: 'rgb(248, 250, 252)' }}>
                                <Group justify="space-between" mb={6}>
                                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: textColors.secondary }}>VOLUME</span>
                                    <span style={{ fontSize: 11, color: textColors.muted }}>Not printed on the vendor document</span>
                                </Group>
                                <Group gap={18} align="baseline">
                                    <div>
                                        <span style={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                            {volume.total == null ? '—' : fmtCbm(volume.total)}
                                        </span>
                                        <span style={{ fontSize: 12, color: textColors.secondary, marginLeft: 4 }}>CBM</span>
                                    </div>
                                    {orderCap && volume.containers && volume.containers.length > 0 && (
                                        <div>
                                            <span style={{ fontSize: 22, fontWeight: 600 }}>≈ {volume.containers.length}</span>
                                            <span style={{ fontSize: 12, color: textColors.secondary, marginLeft: 4 }}>
                                                container{volume.containers.length === 1 ? '' : 's'} at {orderCap} CBM
                                            </span>
                                        </div>
                                    )}
                                </Group>
                                <Text size="12px" c={textColors.secondary} mt={4}>
                                    {!vendorId
                                        ? 'Pick a vendor to estimate containers.'
                                        : orderCap
                                          ? `Cap ${orderCap} CBM, the highest across ${vendorName || 'this vendor'}'s lanes.`
                                          : `No container cap on ${vendorName || 'this vendor'}'s lanes, so no container estimate. Set one under Suppliers.`}
                                </Text>
                                {orderCap && volume.containers && volume.containers.length > 0 && (
                                    <Group gap={6} mt={8}>
                                        {volume.containers.map((c) => (
                                            <div key={c.sequenceNumber} style={{ width: 96 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: textColors.secondary, marginBottom: 3 }}>
                                                    <span>C{c.sequenceNumber}</span>
                                                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtCbm(c.totalMeasurement)}</span>
                                                </div>
                                                <div style={{ height: 12, border: '1px solid rgb(226, 232, 240)', borderRadius: 3, background: '#fff', overflow: 'hidden' }}>
                                                    <div
                                                        style={{
                                                            height: '100%',
                                                            width: `${Math.min(100, (c.totalMeasurement / orderCap) * 100)}%`,
                                                            background: 'rgb(1, 119, 204)',
                                                            opacity: c.totalMeasurement >= orderCap * 0.98 ? 0.75 : 0.35,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </Group>
                                )}
                                {volume.missing.length > 0 && (
                                    <Text size="12px" c="rgb(180, 83, 9)" mt={8}>
                                        {volume.missing.length === 1
                                            ? `1 line has no CBM on the product and is left out of the total: ${volume.missing[0]}.`
                                            : `${volume.missing.length} lines have no CBM on the product and are left out of the total: ${volume.missing.slice(0, 3).join(', ')}${volume.missing.length > 3 ? ` and ${volume.missing.length - 3} more` : ''}.`}{' '}
                                        Add it under Entities → Products.
                                    </Text>
                                )}
                            </div>
                        )}

                        <SectionHeader>ADDITIONAL CHARGES</SectionHeader>
                        <div className="space-y-2">
                            {charges.length === 0 ? (
                                <Text size="12px" c={textColors.muted}>No additional charges.</Text>
                            ) : (
                                charges.map((charge, idx) => (
                                    <Group key={idx} gap={8} wrap="nowrap">
                                        <TextInput placeholder="Charge (e.g. Shipping)" value={charge.label}
                                            onChange={(e) => updateCharge(idx, { label: e.currentTarget.value })} size="xs" style={{ flex: 2 }} />
                                        <NumberInput placeholder="Amount" value={charge.amount}
                                            onChange={(v) => updateCharge(idx, { amount: typeof v === 'number' ? v : '' })} min={0} size="xs" hideControls style={{ flex: 1 }} />
                                        <button type="button" onClick={() => setCharges((prev) => prev.filter((_, i) => i !== idx))}
                                            style={{ border: 'none', background: 'transparent', color: 'rgb(205, 101, 101)', cursor: 'pointer', fontSize: 13 }}>
                                            ✕
                                        </button>
                                    </Group>
                                ))
                            )}
                            <Button variant="subtle" size="xs" onClick={() => setCharges((prev) => [...prev, emptyCharge()])}>
                                + Add charge
                            </Button>
                        </div>

                        <SectionHeader>PAYMENT TERMS</SectionHeader>
                        <PaymentTermsCard
                            vendorId={vendorId}
                            vendorName={vendorName}
                            destinationCountry={destinationCountry}
                            orderTotal={total}
                            orderQuantity={orderUnits}
                            currency={currency}
                            draft={payTerms}
                            onChange={setPayTerms}
                        />

                        <SectionHeader>NOTES &amp; TERMS</SectionHeader>
                        <div className="space-y-2">
                            <Textarea placeholder="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={2} />
                            <Textarea placeholder="Terms & conditions" value={terms} onChange={(e) => setTerms(e.currentTarget.value)} autosize minRows={3} />
                        </div>
                </>
            }
        />

        {/* The same modal the production-order form and Settings use - one set of rules
            about what a prefix is, whichever door it is minted through. */}
        <AddPrefixModal
            opened={isAddingPrefix}
            onClose={() => setIsAddingPrefix(false)}
            documentType="PURCHASE_ORDER"
            existing={poPrefixes}
            onSaved={(saved) => {
                void queryClient.invalidateQueries({
                    queryKey: ['org-config', getOrganizationIdFromStorage()],
                });
                applyPoPrefix(saved);
            }}
        />
        </>
    );
};

// ─── Container ──────────────────────────────────────────────────

/** What the order as a whole is waiting on: the count of lines on each of the two chores. */
const orderNeed = (order: MotherOrder): Need | null => {
    if (order.status === 'DRAFT' || order.status === 'CANCELLED') return null;
    const lines = order.lines ?? [];
    const undated = lines.filter((l) => !l.expectedReadinessDate && l.shipped < l.ordered).length;
    const due = lines.filter(isQcDue).length;
    if (due > 0) return { label: `QC due ×${due}`, tone: 'warn', title: `${due} line${due === 1 ? '' : 's'} with a ready date and no verdict on the unshipped units` };
    if (undated > 0) return { label: `Ready date ×${undated}`, tone: 'warn', title: `${undated} line${undated === 1 ? '' : 's'} without a goods-ready date` };
    return null;
};

/**
 * The page's two sheets. Orders is one row per purchase order; Lines is one row per contract
 * line, where the readiness and QC columns live — they are line facts, and on an order row
 * they were mostly dashes.
 */
type OrdersView = 'orders' | 'lines';
const ORDERS_TABS = [
    { id: 'orders', label: 'Orders' },
    { id: 'lines', label: 'Lines' },
];

/** The Lines sheet's quick worklists: one at a time, or none. */
type LinesQuick = 'none' | 'noReadyDate' | 'qcPending';

/**
 * A row of the Lines sheet: one contract line with its order beside it. The fields the grid
 * reads are flattened onto the row so the per-column filters, sorts and search see plain
 * values rather than a nested object.
 */
type LineRow = {
    key: string;
    order: MotherOrder;
    line: MotherOrderLine;
    orderNumber: string;
    vendorName: string | null;
    description: string;
    ordered: number;
    inProduction: number;
    inTransit: number;
    received: number;
    expectedReadinessDate: string | null;
    qcPassedQty: number | null;
    qcRejectedQty: number | null;
    qcSource: string;
    shippable: number;
    need: string;
};

const QC_SOURCE_LABEL: Record<NonNullable<MotherOrderLine['qcSource']>, string> = {
    BRAND: 'Brand',
    ASSUMED: 'Assumed',
};

const toLineRow = (order: MotherOrder, line: MotherOrderLine): LineRow => ({
    // lineItemId is 'default' on every unsplit SKU, so the item id is part of the key.
    key: `${order.id}::${line.itemId}::${line.lineItemId}`,
    order,
    line,
    orderNumber: order.orderNumber,
    vendorName: order.vendorName,
    description: line.description || line.itemId,
    ordered: line.ordered,
    inProduction: line.inProduction,
    inTransit: line.inTransit,
    received: line.received,
    expectedReadinessDate: line.expectedReadinessDate,
    qcPassedQty: line.qcPassedQty,
    qcRejectedQty: line.qcRejectedQty,
    qcSource: line.qcSource ? QC_SOURCE_LABEL[line.qcSource] : '—',
    shippable: line.shippable,
    need: lineNeed(line).label,
});

/**
 * The quick worklists, scoped as the emailed QC-due report scopes them: a cancelled order's
 * lines are off every list.
 */
const LINES_QUICK_PREDICATE: Record<LinesQuick, (row: LineRow) => boolean> = {
    none: () => true,
    noReadyDate: ({ order, line }) =>
        order.status !== 'CANCELLED' && !line.expectedReadinessDate && line.shipped < line.ordered,
    qcPending: ({ order, line }) => order.status !== 'CANCELLED' && isQcDue(line),
};

const LINES_EMPTY_COPY: Record<LinesQuick, string> = {
    none: 'No order lines yet. Click "Create Purchase Order" to author your first one.',
    noReadyDate: 'Every line has a goods-ready date, or is already on a shipment.',
    qcPending:
        'No lines are waiting on QC. A line is due once it has a ready date, until the brand records a verdict or a shipment carries the units.',
};

const numCell = (v: number, muteZero = false) => (
    <span style={{ fontSize: 13, color: muteZero && !(v > 0) ? textColors.muted : textColors.primary, fontVariantNumeric: 'tabular-nums' }}>
        {num(v)}
    </span>
);

const OrdersContainer: FC = () => {
    const queryClient = useQueryClient();
    const cbmEnabled = useCbmEnabled();
    /** Which sheet is open and which quick list is on, remembered across visits like filters. */
    const [view, setView] = usePersistentState<OrdersView>('orders.view', 'orders');
    const [quick, setQuick] = usePersistentState<LinesQuick>('orders.lines.quick', 'none');
    const [formOpen, setFormOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState<MotherOrder | null>(null);
    const [detailId, setDetailId] = useState<string | null>(null);
    const [sendOnOpen, setSendOnOpen] = useState(false);
    const [shipmentOpen, setShipmentOpen] = useState(false);
    const [shipmentPrefill, setShipmentPrefill] = useState<AddShipmentPrefill | undefined>(undefined);
    const [workOrderPrefill, setWorkOrderPrefill] = useWorkOrderPrefill(() => {
        setEditingOrder(null);
        setFormOpen(true);
    });
    const returnToWorkOrder = useReturnToWorkOrder();

    const openCreate = () => {
        setEditingOrder(null);
        setWorkOrderPrefill(null);
        setFormOpen(true);
    };
    const openEdit = (order: MotherOrder) => {
        setDetailId(null);
        setEditingOrder(order);
        setFormOpen(true);
    };

    const { data: orders, isLoading } = useQuery(['mother-orders'], () => listMotherOrders());

    /*
     * Arrived from a receipt that amended an order: open that order, and its send dialog when
     * asked, then forget the request so a refresh does not replay it.
     */
    const location = useLocation();
    const navigate = useNavigate();
    const arrival = location.state as { openOrderNumber?: string; send?: boolean } | null;
    useEffect(() => {
        if (!arrival?.openOrderNumber || !orders) return;
        const target = orders.find((candidate) => candidate.orderNumber === arrival.openOrderNumber);
        if (target) {
            setDetailId(target.id);
            setSendOnOpen(!!arrival.send);
        }
        void navigate(location.pathname, { replace: true, state: null });
    }, [arrival, orders, navigate, location.pathname]);
    const { data: payablesView } = useQuery(['payables', true], () => getPayables(true));
    const payablesIndex = useMemo(() => summarizePayables(payablesView), [payablesView]);

    const { data: detailOrder, isFetching: detailLoading } = useQuery(
        ['mother-order', detailId],
        () => getMotherOrder(detailId as string),
        { enabled: detailId !== null },
    );

    const { data: vendors } = useQuery(['vendors-for-po'], async () => {
        const rows = await ReparoApi<Array<VendorOption & { vendorRole?: string | null; capabilities?: VendorCapability[] | null }>>({
            method: 'GET',
            url: '/vendors?limit=200',
        });
        // A purchase order is placed with a SUPPLIER (FG / RM). Credit partners are payees
        // (Pay-via selector), and a pure manufacturer is a production-order counterparty —
        // neither belongs here. Untagged vendors stay visible.
        return (rows ?? []).filter(
            (v) => v.vendorId && v.vendorRole !== 'Credit partner' && isSupplierVendor(v),
        );
    });

    const { data: skuCatalog } = useQuery(['po-sku-catalog'], async () => {
        const [materials, products] = await Promise.all([getBomMaterialPicks(), getBomProductPicks()]);
        const opts: SkuOption[] = [];
        // Dedupe by SKU (materials win) — a SKU present in both tables would crash the Mantine Select
        const seen = new Set<string>();
        for (const m of materials) {
            // A material with no SKU of its own is keyed by its id. Whole catalogues carry
            // none (Troovy, koparo), and dropping them made every raw and packing material
            // unorderable here. The key is only the picker's value and the CSV's match
            // column; saving sends the material id.
            const key = m.sku || m.materialId;
            if (key && !seen.has(key)) {
                seen.add(key);
                opts.push({
                    sku: key,
                    name: m.name,
                    kind: 'material',
                    id: m.materialId,
                    gstRate: m.gstRate ?? null,
                    hsnCode: m.hsnCode ?? null,
                });
            }
        }
        for (const p of products) {
            if (p.sku && !seen.has(p.sku)) {
                seen.add(p.sku);
                opts.push({
                    sku: p.sku,
                    name: p.name,
                    kind: 'product',
                    id: p.productId,
                    gstRate: p.gstRate ?? null,
                    hsnCode: p.hsnCode ?? null,
                    volumeCbm: p.volumeCbm ?? null,
                    unitsPerCase: p.unitsPerCase ?? null,
                });
            }
        }
        return opts;
    });

    const refreshAll = () => {
        void queryClient.invalidateQueries(['mother-orders']);
        if (detailId) void queryClient.invalidateQueries(['mother-order', detailId]);
    };

    const rows = useMemo(() => (orders ?? []) as MotherOrder[], [orders]);

    /** Every contract line of every order, as the Lines sheet's rows; then the quick list on top. */
    const allLineRows = useMemo<LineRow[]>(
        () => rows.flatMap((order) => (order.lines ?? []).map((line) => toLineRow(order, line))),
        [rows],
    );
    const lineRows = useMemo(() => allLineRows.filter(LINES_QUICK_PREDICATE[quick]), [allLineRows, quick]);

    /** The order number with the way into the order beside it, on both sheets. */
    const renderOrderCell = (orderNumber: string, orderId: string) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: textColors.primary }}>{orderNumber}</span>
            <Tooltip label="Work on this order" withArrow>
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label={`Work on order ${orderNumber}`}
                    onClick={(e) => {
                        // The row opens the order too; the icon is the visible way in.
                        e.stopPropagation();
                        setDetailId(orderId);
                    }}
                >
                    <ArrowSquareOut size={15} />
                </ActionIcon>
            </Tooltip>
        </span>
    );

    const columns: DataSheetColumn<MotherOrder>[] = useMemo(
        () => [
            { key: 'orderNumber', header: 'Order', width: 200, sticky: true,
              render: (v: string, r: MotherOrder) => renderOrderCell(v, r.id) },
            { key: 'vendorName', header: 'Vendor', width: 220,
              render: (v: string | null) => <span style={{ fontSize: 13, color: v ? textColors.primary : textColors.muted }}>{v || 'No vendor'}</span> },
            { key: 'poDate', header: 'Order Date', width: 130,
              render: (v: string) => <span style={{ fontSize: 13, color: textColors.primary }}>{formatDateDisplay(v)}</span> },
            { key: 'expectedDeliveryDate', header: 'Expected Delivery', width: 150,
              render: (v: string | null) => <span style={{ fontSize: 13, color: v ? textColors.primary : textColors.muted }}>{v ? formatDateDisplay(v) : '—'}</span> },
            { key: 'status', header: 'Status', width: 160, render: (v: string) => <StatusBadge status={v} /> },
            { key: 'paymentTerms', header: 'Payment Terms', width: 170,
                render: (_v: unknown, row: MotherOrder) =>
                    row.paymentSchedule?.template?.length
                        ? (row.paymentSchedule.termsName ?? 'Custom')
                        : '—' },
            { key: 'payments', header: 'Payments', width: 170,
              render: (_: unknown, r: MotherOrder) => <PaymentChip summary={payablesIndex.byMotherId.get(r.id)} /> },
            ...(cbmEnabled
                ? [{ key: 'totalCbm', header: 'CBM', width: 110, align: 'right' as const,
                      render: (v: number | null) => <span style={{ fontSize: 13, color: v == null ? textColors.muted : textColors.primary, fontVariantNumeric: 'tabular-nums' }}>{v == null ? '—' : fmtCbm(v)}</span> }]
                : []),
            { key: 'orderedUnits', header: 'Ordered', width: 125, align: 'right' as const,
              render: (v: number) => <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{num(v)}</span> },
            { key: 'inProductionUnits', header: 'In Production', width: 155, align: 'right' as const,
              render: (v: number) => numCell(v) },
            { key: 'inTransitUnits', header: 'In Transit', width: 135, align: 'right' as const,
              render: (v: number) => numCell(v) },
            { key: 'receivedUnits', header: 'Received', width: 125, align: 'right' as const,
              render: (v: number) => numCell(v, true) },
            { key: 'need', header: 'Needs', width: 130,
              render: (_v: unknown, r: MotherOrder) => {
                  const need = orderNeed(r);
                  return need ? <NeedChip need={need} /> : <span style={{ fontSize: 13, color: textColors.muted }}>—</span>;
              } },
            { key: 'total', header: 'Total', width: 140, align: 'right' as const,
              render: (v: number, r: MotherOrder) => <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(v, r.currency)}</span> },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps -- renderOrderCell only closes over setDetailId, which is stable
        [payablesIndex, cbmEnabled],
    );

    const lineColumns: DataSheetColumn<LineRow>[] = useMemo(
        () => [
            { key: 'orderNumber', header: 'Order', width: 200, sticky: true,
              render: (v: string, r: LineRow) => renderOrderCell(v, r.order.id) },
            { key: 'vendorName', header: 'Vendor', width: 200, sticky: true,
              render: (v: string | null) => <span style={{ fontSize: 13, color: v ? textColors.primary : textColors.muted }}>{v || 'No vendor'}</span> },
            { key: 'description', header: 'Item', width: 240,
              render: (v: string, r: LineRow) => (
                  <span style={{ fontSize: 13, color: textColors.primary, lineHeight: 1.25 }}>
                      {v}
                      {v !== r.line.itemId && (
                          // The id beneath the name: two lines can share a name.
                          <span style={{ display: 'block', fontSize: 11, color: textColors.muted, fontVariantNumeric: 'tabular-nums' }}>
                              {r.line.itemId}
                          </span>
                      )}
                  </span>
              ) },
            { key: 'ordered', header: 'Ordered', width: 110, align: 'right' as const,
              render: (v: number) => <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{num(v)}</span> },
            { key: 'inProduction', header: 'In production', width: 130, align: 'right' as const,
              render: (v: number) => numCell(v) },
            { key: 'inTransit', header: 'In transit', width: 110, align: 'right' as const,
              render: (v: number) => numCell(v) },
            { key: 'received', header: 'Received', width: 110, align: 'right' as const,
              render: (v: number) => numCell(v, true) },
            { key: 'expectedReadinessDate', header: 'Ready on', width: 130,
              render: (v: string | null) => <span style={{ fontSize: 13, color: v ? textColors.primary : textColors.muted, fontVariantNumeric: 'tabular-nums' }}>{v ? formatDateDisplay(v) : '—'}</span> },
            // QC quantities read the same whoever wrote them: a shipment's assumed figure is
            // a figure, and the source column says whose it is.
            { key: 'qcPassedQty', header: 'QC passed', width: 110, align: 'right' as const,
              render: (v: number | null) => <span style={{ fontSize: 13, color: v == null ? textColors.muted : textColors.primary, fontVariantNumeric: 'tabular-nums' }}>{v == null ? '—' : num(v)}</span> },
            { key: 'qcRejectedQty', header: 'QC rejected', width: 110, align: 'right' as const,
              render: (v: number | null) => <span style={{ fontSize: 13, color: v == null ? textColors.muted : textColors.primary, fontVariantNumeric: 'tabular-nums' }}>{v == null ? '—' : num(v)}</span> },
            { key: 'qcSource', header: 'QC source', width: 110,
              render: (v: string, r: LineRow) => <span style={{ fontSize: 13, color: r.line.qcSource === 'BRAND' ? textColors.primary : textColors.muted }}>{v}</span> },
            { key: 'shippable', header: 'Shippable', width: 100, align: 'right' as const,
              render: (v: number) => <span style={{ fontSize: 13, fontWeight: 600, color: v > 0 ? textColors.primary : textColors.muted, fontVariantNumeric: 'tabular-nums' }}>{num(v)}</span> },
            { key: 'need', header: 'Needs', width: 130,
              render: (_v: unknown, r: LineRow) => <NeedChip need={lineNeed(r.line)} /> },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps -- renderOrderCell only closes over setDetailId, which is stable
        [],
    );

    const toggleQuick = (which: Exclude<LinesQuick, 'none'>) => (checked: boolean) =>
        setQuick(checked ? which : 'none');

    const sheetTabs = {
        tabs: ORDERS_TABS,
        activeTab: view,
        onTabChange: (id: string) => setView(id === 'lines' ? 'lines' : 'orders'),
    };

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <Group justify="space-between" align="flex-start">
                <PageHeader icon="📦" title="Create Purchase Order" subtitle="Author purchase orders and track their shipments" />
                <Button onClick={openCreate}>Create Purchase Order</Button>
            </Group>

            <Box>
                {view === 'orders' ? (
                    <DataSheet<MotherOrder>
                        key="orders"
                        {...sheetTabs}
                        data={rows}
                        columns={columns}
                        isLoading={isLoading}
                        getRowKey={(row) => row.id}
                        title="Purchase orders"
                        subtitle={`${rows.length} orders`}
                        enableSearch
                        searchPlaceholder="Search by order number..."
                        onRowClick={(row) => setDetailId(row.id)}
                        emptyState={
                            <Text size="13px" c="dimmed" ta="center" p={32}>
                                No orders yet. Click "Create Purchase Order" to author your first one.
                            </Text>
                        }
                    />
                ) : (
                    <DataSheet<LineRow>
                        key="lines"
                        {...sheetTabs}
                        data={lineRows}
                        columns={lineColumns}
                        isLoading={isLoading}
                        getRowKey={(row) => row.key}
                        title="Purchase order lines"
                        subtitle={
                            quick === 'none'
                                ? `${lineRows.length} line${lineRows.length === 1 ? '' : 's'}`
                                : `${lineRows.length} of ${allLineRows.length} line${allLineRows.length === 1 ? '' : 's'}`
                        }
                        enableSearch
                        searchPlaceholder="Search by order, vendor or item..."
                        onRowClick={(row) => setDetailId(row.order.id)}
                        toolbarControls={
                            <Group gap={8} wrap="nowrap">
                                <Chip
                                    size="xs"
                                    checked={quick === 'noReadyDate'}
                                    onChange={toggleQuick('noReadyDate')}
                                    title="Lines with units still to ship and no goods-ready date"
                                >
                                    No ready date
                                </Chip>
                                <Chip
                                    size="xs"
                                    checked={quick === 'qcPending'}
                                    onChange={toggleQuick('qcPending')}
                                    title="Lines with a ready date, unshipped units and no brand QC verdict"
                                >
                                    QC pending
                                </Chip>
                            </Group>
                        }
                        emptyState={
                            <Text size="13px" c="dimmed" ta="center" p={32}>
                                {LINES_EMPTY_COPY[quick]}
                            </Text>
                        }
                    />
                )}
            </Box>


            <RichOrderFormModal
                // A work order hand-off opens the form on arrival; it waits for the catalog so
                // the form starts once, as a click on Create would, and nothing it seeds (the
                // brand's Bill To, the vendor) is reset when the catalog lands.
                opened={formOpen && (!workOrderPrefill || !!skuCatalog)}
                existingOrder={editingOrder}
                workOrderPrefill={workOrderPrefill}
                onClose={() => {
                    setFormOpen(false);
                    setEditingOrder(null);
                    setWorkOrderPrefill(null);
                }}
                vendors={vendors ?? []}
                skuOptions={skuCatalog ?? NO_SKU_OPTIONS}
                onSaved={() => {
                    void queryClient.invalidateQueries(['mother-orders']);
                    if (editingOrder)
                        void queryClient.invalidateQueries(['mother-order', editingOrder.id]);
                    if (workOrderPrefill && !editingOrder) returnToWorkOrder(workOrderPrefill.workOrderId);
                }}
            />

            <OrderDetailModal
                opened={detailId !== null}
                order={detailOrder ?? null}
                loading={detailLoading}
                sendOnOpen={sendOnOpen}
                onSendOpened={() => setSendOnOpen(false)}
                onClose={() => setDetailId(null)}
                onChanged={refreshAll}
                onAddShipment={(prefill) => {
                    setShipmentPrefill(prefill);
                    setShipmentOpen(true);
                }}
                onEdit={openEdit}
            />

            <AddShipmentModal
                opened={shipmentOpen}
                order={detailOrder ?? null}
                prefill={shipmentPrefill}
                onClose={() => setShipmentOpen(false)}
                onAdded={refreshAll}
            />
        </div>
    );
};

export default OrdersContainer;
